/**
 * Protocolos Iniciales de Exocortex
 *
 * Basados en las necesidades específicas documentadas:
 * - Sistema por capas (supervivencia/mantenimiento/crecimiento)
 * - Fundamentos científicos para cada protocolo
 * - Adaptado para TDAH/TEA
 */

import type { Protocol } from '@/core/types';

/**
 * Protocolos predefinidos
 * Diseñados con evidencia científica y adaptados a neurodiversidad
 */
export const INITIAL_PROTOCOLS: Protocol[] = [
  // ============================================
  // CAPA DE SUPERVIVENCIA (SURVIVAL)
  // ============================================

  {
    id: 'prot-bio-vital',
    title: 'Soporte Vital Biológico',
    description: 'Funciones básicas del cuerpo que no pueden posponerse',
    rationale:
      'El cerebro consume ~20% de la energía del cuerpo. Sin hidratación y medicación correcta, la función ejecutiva (ya comprometida en TDAH) se degrada significativamente. La deshidratación del 2% reduce la capacidad cognitiva hasta un 25%.',
    frequency: 'daily',
    layer: 'survival',
    priority: 'critical',
    category: 'health',
    timeEstimate: 10,
    timeBlock: 'morning',
    idealTime: '07:30',
    subtasks: [
      {
        id: 'sub-med',
        label: 'Tomar Escitalopram 20mg (07:00-07:30)',
        isDone: false,
        order: 0,
        estimatedMinutes: 1,
        isCritical: true,
        scientificNote: 'Medicación matutina - no improvisar dosis ni horarios',
      },
      {
        id: 'sub-water-1',
        label: 'Primer vaso grande de agua',
        isDone: false,
        order: 1,
        estimatedMinutes: 2,
        scientificNote: 'Rehidratar tras 6-8h sin líquidos',
      },
      {
        id: 'sub-water-2',
        label: 'Segundo vaso de agua (antes de mediodía)',
        isDone: false,
        order: 2,
        estimatedMinutes: 2,
        scientificNote: '500ml mejora memoria de trabajo y reduce fatiga (estudio 2019)',
      },
      {
        id: 'sub-light',
        label: 'Exposición a luz natural (3-10 min)',
        isDone: false,
        order: 3,
        estimatedMinutes: 5,
        scientificNote: 'Regula cortisol matutino y ritmo circadiano',
      },
    ],
    tags: ['salud', 'medicación', 'hidratación', 'laboral'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: true,
    reminderTime: '07:30',
    emoji: '💊',
  },

  {
    id: 'prot-pets',
    title: 'Protocolo Philippa & Fjor',
    description: 'Cuidado de dependientes (mascotas)',
    rationale:
      'Los animales dependientes no pueden esperar. El estrés por necesidades no cubiertas afecta su salud y tu tranquilidad. La rutina consistente reduce comportamientos problemáticos.',
    frequency: 'daily',
    layer: 'survival',
    priority: 'critical',
    category: 'pets',
    timeEstimate: 15,
    timeBlock: 'morning',
    idealTime: '07:45',
    subtasks: [
      {
        id: 'sub-litter',
        label: 'Limpiar areneros',
        isDone: false,
        order: 0,
        estimatedMinutes: 5,
        isCritical: true,
        scientificNote: 'Diario sin excepción - previene problemas urinarios',
      },
      {
        id: 'sub-water-pets',
        label: 'Agua fresca disponible',
        isDone: false,
        order: 1,
        estimatedMinutes: 2,
        scientificNote: 'Gatos necesitan ~60ml/kg/día. Agua estancada causa rechazo (Cornell Vet)',
      },
      {
        id: 'sub-food-pets',
        label: 'Comida según rutina',
        isDone: false,
        order: 2,
        estimatedMinutes: 3,
        scientificNote: 'Horario consistente reduce estrés felino y problemas digestivos',
      },
      {
        id: 'sub-check-pets',
        label: 'Revisión visual rápida (ojos, pelaje, comportamiento)',
        isDone: false,
        order: 3,
        estimatedMinutes: 5,
        scientificNote: 'Detectar cambios temprano evita emergencias',
      },
    ],
    tags: ['mascotas', 'philippa', 'fjor', 'laboral'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: true,
    reminderTime: '08:00',
    emoji: '🐱',
  },

  {
    id: 'prot-anchor',
    title: 'Anclaje Cognitivo',
    description: 'Dirección del día y prevención de dispersión',
    rationale:
      'El TDAH dificulta la priorización. Sin un "norte" claro, la energía se dispersa en urgencias falsas. 5 minutos de intención reducen horas de deriva cognitiva.',
    frequency: 'daily',
    layer: 'survival',
    priority: 'critical',
    category: 'self-care',
    timeEstimate: 5,
    timeBlock: 'morning',
    idealTime: '08:45',
    subtasks: [
      {
        id: 'sub-intent',
        label: '¿Qué haría que hoy valiera la pena?',
        isDone: false,
        order: 0,
        estimatedMinutes: 1,
        scientificNote: 'Una pregunta que filtra prioridades',
      },
      {
        id: 'sub-risk',
        label: '¿Cuál es el riesgo principal de hoy? (dispersión, rumiación, etc.)',
        isDone: false,
        order: 1,
        estimatedMinutes: 1,
        scientificNote: 'Anticipar riesgos reduce rumiación 45% (meta-análisis PMC 2024)',
      },
      {
        id: 'sub-top3',
        label: 'Escribir 3 prioridades (verbo + resultado)',
        isDone: false,
        order: 2,
        estimatedMinutes: 3,
        scientificNote: 'Ej: "Entregar X", "Estudiar Y 30min"',
      },
    ],
    tags: ['foco', 'planificación', 'mente'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: true,
    reminderTime: '09:00',
    emoji: '🎯',
  },

  {
    id: 'prot-hygiene-am',
    title: 'Higiene Mañana',
    description: 'Cuidado personal matutino',
    rationale:
      'La higiene afecta la autopercepción y energía. "Estar despierta" con ropa adecuada es un cambio de estado que facilita la activación.',
    frequency: 'daily',
    layer: 'survival',
    priority: 'high',
    category: 'hygiene',
    timeEstimate: 10,
    timeBlock: 'morning',
    idealTime: '07:15',
    subtasks: [
      {
        id: 'sub-face',
        label: 'Lavar cara',
        isDone: false,
        order: 0,
        estimatedMinutes: 2,
        scientificNote: 'Limpiador suave 30seg, agua tibia. No es necesario AM si piel seca (AAD)',
      },
      {
        id: 'sub-teeth-am',
        label: 'Cepillar dientes',
        isDone: false,
        order: 1,
        estimatedMinutes: 3,
        scientificNote: '2 min mínimo (ADA)',
      },
      {
        id: 'sub-clothes',
        label: 'Ropa de "estar despierta"',
        isDone: false,
        order: 2,
        estimatedMinutes: 5,
        scientificNote: 'Cambio de estado psicológico',
      },
    ],
    tags: ['higiene', 'autocuidado'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '🪥',
  },

  {
    id: 'prot-skincare-am',
    title: 'Skincare Mañana',
    description: 'Cuidado de piel matutino con protección solar',
    rationale:
      'Hasta el 80% de los signos de envejecimiento provienen de exposición solar sin protección (AAD). El protector solar SPF 30+ es el producto anti-edad #1 según dermatólogos. La hidratación mantiene la barrera cutánea funcional.',
    frequency: 'daily',
    layer: 'survival',
    priority: 'high',
    category: 'hygiene',
    timeEstimate: 5,
    timeBlock: 'morning',
    idealTime: '07:20',
    subtasks: [
      {
        id: 'sub-moisturizer',
        label: 'Aplicar hidratante (ácido hialurónico/glicerina)',
        isDone: false,
        order: 0,
        estimatedMinutes: 2,
        scientificNote: 'Humectantes mantienen piel hidratada y plump',
      },
      {
        id: 'sub-sunscreen',
        label: 'Protector solar SPF 30+ (½ cucharadita cara+cuello)',
        isDone: false,
        order: 1,
        estimatedMinutes: 3,
        isCritical: true,
        scientificNote: 'AAD: SPF 30+, amplio espectro, reaplicar cada 2h si hay exposición',
      },
    ],
    tags: ['skincare', 'protección', 'autocuidado'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '☀️',
  },

  {
    id: 'prot-hygiene-pm',
    title: 'Higiene Noche',
    description: 'Cuidado personal nocturno esencial',
    rationale:
      'Cepillar dientes antes de dormir es crítico: la saliva disminuye durante el sueño, reduciendo protección contra bacterias. La ADA recomienda 2 veces al día mínimo.',
    frequency: 'daily',
    layer: 'survival',
    priority: 'high',
    category: 'hygiene',
    timeEstimate: 5,
    timeBlock: 'evening',
    idealTime: '22:00',
    subtasks: [
      {
        id: 'sub-teeth-pm',
        label: 'Cepillar dientes',
        isDone: false,
        order: 0,
        estimatedMinutes: 3,
        isCritical: true,
        scientificNote: '2 min mínimo. Usar hilo dental idealmente.',
      },
      {
        id: 'sub-floss',
        label: 'Hilo dental (opcional pero recomendado)',
        isDone: false,
        order: 1,
        estimatedMinutes: 2,
        scientificNote: 'ADA: reduce enfermedad periodontal',
      },
    ],
    tags: ['higiene', 'dientes', 'noche'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: true,
    reminderTime: '22:00',
    emoji: '🪥',
  },

  {
    id: 'prot-skincare-pm',
    title: 'Skincare Noche',
    description: 'Cuidado de piel nocturno con hidratación',
    rationale:
      'La piel se repara durante la noche. Limpiar protector solar y contaminantes + aplicar hidratante nocturno optimiza la regeneración celular.',
    frequency: 'daily',
    layer: 'maintenance',
    priority: 'medium',
    category: 'hygiene',
    timeEstimate: 5,
    timeBlock: 'evening',
    idealTime: '22:05',
    subtasks: [
      {
        id: 'sub-cleanser',
        label: 'Limpiar cara (remover protector solar y suciedad)',
        isDone: false,
        order: 0,
        estimatedMinutes: 2,
        scientificNote: 'Limpiador suave, sin sulfatos agresivos',
      },
      {
        id: 'sub-night-cream',
        label: 'Aplicar crema hidratante nocturna',
        isDone: false,
        order: 1,
        estimatedMinutes: 3,
        scientificNote: 'Ceramidas o ácido hialurónico para reparación',
      },
    ],
    tags: ['skincare', 'hidratación', 'noche'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '🌙',
  },

  {
    id: 'prot-supplements',
    title: 'Suplementación Vegana',
    description: 'Vitaminas y nutrientes críticos para dieta vegana',
    rationale:
      'El 92% de veganos tienen deficiencia de B12 (no hay fuente vegetal confiable). Solo 8% del ALA vegetal se convierte en EPA/DHA. 41% de americanos tienen déficit de Vitamina D. La suplementación es necesaria, no opcional.',
    frequency: 'daily',
    layer: 'survival',
    priority: 'high',
    category: 'health',
    timeEstimate: 2,
    timeBlock: 'morning',
    idealTime: '07:35',
    subtasks: [
      {
        id: 'sub-b12',
        label: 'Vitamina B12 (2500mcg semanal o dosis diaria)',
        isDone: false,
        order: 0,
        estimatedMinutes: 1,
        isCritical: true,
        scientificNote: 'Crítico: no hay fuente vegetal. Deficiencia causa daño neurológico.',
      },
      {
        id: 'sub-d3',
        label: 'Vitamina D3 vegana (1000-2000 UI según época)',
        isDone: false,
        order: 1,
        estimatedMinutes: 1,
        scientificNote: 'Importante para sistema inmune y huesos. Mayor dosis en invierno.',
      },
      {
        id: 'sub-omega3',
        label: 'Omega-3 algal (EPA+DHA)',
        isDone: false,
        order: 2,
        estimatedMinutes: 1,
        scientificNote: 'La conversión de ALA a DHA es muy baja (4%). Suplementar directo.',
      },
    ],
    tags: ['suplementos', 'vegano', 'salud', 'laboral'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: true,
    reminderTime: '07:35',
    emoji: '💊',
  },

  {
    id: 'prot-hydration-pm',
    title: 'Hidratación Tarde',
    description: 'Mantener hidratación durante el día',
    rationale:
      'La deshidratación acumulada durante el día afecta la concentración y aumenta la fatiga. 1-2 vasos adicionales en la tarde previenen el déficit.',
    frequency: 'daily',
    layer: 'survival',
    priority: 'high',
    category: 'health',
    timeEstimate: 2,
    timeBlock: 'afternoon',
    idealTime: '15:00',
    subtasks: [
      {
        id: 'sub-water-pm-1',
        label: 'Vaso de agua (tarde)',
        isDone: false,
        order: 0,
        estimatedMinutes: 1,
        scientificNote: 'Hidratación constante >impacto puntual. Meta: 2-3L/día total',
      },
      {
        id: 'sub-water-pm-2',
        label: 'Segundo vaso (opcional pero recomendado)',
        isDone: false,
        order: 1,
        estimatedMinutes: 1,
        scientificNote: 'Deshidratación leve afecta concentración y humor',
      },
    ],
    tags: ['hidratación', 'salud'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: true,
    reminderTime: '15:00',
    emoji: '💧',
  },

  // --- CRONONUTRICIÓN: Comidas separadas por bloque horario ---

  {
    id: 'prot-breakfast',
    title: 'Desayuno Vegano',
    description: 'Primera comida - abre ventana de alimentación',
    rationale:
      'El desayuno rompe el ayuno nocturno y activa el metabolismo. Proteína vegetal + fibra estabilizan glucosa y mejoran función cognitiva. Comer temprano rinde mejor que tarde (Harvard/DASH).',
    frequency: 'daily',
    layer: 'survival',
    priority: 'high',
    category: 'health',
    timeEstimate: 20,
    timeBlock: 'morning',
    idealTime: '07:30',
    subtasks: [
      {
        id: 'sub-breakfast-prep',
        label: 'Preparar desayuno',
        isDone: false,
        order: 0,
        estimatedMinutes: 10,
        scientificNote: 'Prep simple reduce fricción: avena overnight, batido, tostadas',
      },
      {
        id: 'sub-breakfast-eat',
        label: 'Comer con calma (sin pantallas)',
        isDone: false,
        order: 1,
        estimatedMinutes: 10,
        isCritical: true,
        scientificNote: 'Proteína + fibra: avena con frutos secos, pan integral con hummus/palta, tofu revuelto',
      },
    ],
    tags: ['alimentación', 'crononutrición', 'vegano', 'mañana', 'laboral'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: true,
    reminderTime: '07:30',
    emoji: '🥣',
  },

  {
    id: 'prot-lunch',
    title: 'Almuerzo Principal',
    description: 'Comida más grande del día - máxima energía',
    rationale:
      'El almuerzo debe ser la comida más sustanciosa. El metabolismo está más activo al mediodía. Plato: 50% verduras, 25% proteína vegetal, 25% carbohidrato integral.',
    frequency: 'daily',
    layer: 'survival',
    priority: 'high',
    category: 'health',
    timeEstimate: 30,
    timeBlock: 'afternoon',
    idealTime: '13:00',
    subtasks: [
      {
        id: 'sub-lunch-prep',
        label: 'Preparar/calentar almuerzo',
        isDone: false,
        order: 0,
        estimatedMinutes: 10,
        scientificNote: 'Batch cooking facilita adherencia. Prep domingo = menos decisiones',
      },
      {
        id: 'sub-lunch-eat',
        label: 'Comer almuerzo completo',
        isDone: false,
        order: 1,
        estimatedMinutes: 20,
        isCritical: true,
        scientificNote: '50% verduras, 25% proteína vegetal (legumbres/tofu/tempeh), 25% carbohidrato integral',
      },
    ],
    tags: ['alimentación', 'crononutrición', 'vegano', 'tarde'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: true,
    reminderTime: '13:00',
    emoji: '🥗',
  },

  {
    id: 'prot-snack',
    title: 'Colación Tarde',
    description: 'Snack opcional antes de estudio/ejercicio',
    rationale:
      'Solo necesaria si hay actividad cognitiva o física después. Evita el bajón de las 17-18h. Mantiene glucosa estable hasta la cena.',
    frequency: 'daily',
    layer: 'maintenance',
    priority: 'low',
    category: 'health',
    timeEstimate: 10,
    timeBlock: 'afternoon',
    idealTime: '17:30',
    subtasks: [
      {
        id: 'sub-snack-eat',
        label: 'Colación ligera',
        isDone: false,
        order: 0,
        estimatedMinutes: 10,
        scientificNote: 'Opcional: fruta + frutos secos, hummus con verduras, yogur vegetal',
      },
    ],
    tags: ['alimentación', 'crononutrición', 'vegano', 'tarde'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '🍎',
  },

  {
    id: 'prot-dinner',
    title: 'Cena Ligera',
    description: 'Última comida - cierra ventana de alimentación a las 20:30',
    rationale:
      'La cena debe ser más liviana que el almuerzo. Comer tarde afecta el sueño y el metabolismo. Ventana de alimentación cierra a las 20:30, iniciando ayuno natural de ~11h.',
    frequency: 'daily',
    layer: 'survival',
    priority: 'high',
    category: 'health',
    timeEstimate: 20,
    timeBlock: 'evening',
    idealTime: '20:00',
    subtasks: [
      {
        id: 'sub-dinner-prep',
        label: 'Preparar cena ligera',
        isDone: false,
        order: 0,
        estimatedMinutes: 10,
        scientificNote: 'Cena <500kcal ideal. Evitar comida pesada 2-3h antes de dormir',
      },
      {
        id: 'sub-dinner-eat',
        label: 'Cenar antes de las 20:30',
        isDone: false,
        order: 1,
        estimatedMinutes: 10,
        isCritical: true,
        scientificNote: 'Sopa, ensalada con proteína, verduras salteadas. CIERRA ventana de alimentación.',
      },
    ],
    tags: ['alimentación', 'crononutrición', 'vegano', 'noche'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: true,
    reminderTime: '20:00',
    emoji: '🍲',
  },

  {
    id: 'prot-sleep-ritual',
    title: 'Ritual de Sueño',
    description: 'Preparación para dormir - no negociable',
    rationale:
      'El sueño de 7-9h con horario constante es base de toda función cognitiva. La irregularidad horaria aumenta riesgo cardiovascular y deterioro cognitivo. El ritual prepara al sistema nervioso.',
    frequency: 'daily',
    layer: 'survival',
    priority: 'critical',
    category: 'health',
    timeEstimate: 20,
    timeBlock: 'evening',
    idealTime: '22:00',
    subtasks: [
      {
        id: 'sub-screens-off',
        label: 'Pantallas en modo nocturno o fuera (22:00)',
        isDone: false,
        order: 0,
        estimatedMinutes: 1,
        scientificNote: 'Luz azul inhibe melatonina 1-2h antes de dormir',
      },
      {
        id: 'sub-med-night',
        label: 'Tomar Topiramato 25mg (22:00)',
        isDone: false,
        order: 1,
        estimatedMinutes: 1,
        isCritical: true,
        scientificNote: 'Medicación nocturna - no improvisar dosis ni horarios',
      },
      {
        id: 'sub-tomorrow-prep',
        label: 'Preparar mañana: ropa, primer paso de tarea crítica',
        isDone: false,
        order: 2,
        estimatedMinutes: 5,
        scientificNote: 'Reduce fricción de arranque del día siguiente',
      },
      {
        id: 'sub-water-bedside',
        label: 'Vaso de agua en velador para mañana',
        isDone: false,
        order: 3,
        estimatedMinutes: 1,
        scientificNote: 'Objeto visible = no depender de memoria',
      },
      {
        id: 'sub-sleep-time',
        label: 'Acostarse (22:30-23:30)',
        isDone: false,
        order: 4,
        estimatedMinutes: 5,
        isCritical: true,
        scientificNote: 'Horario constante sincroniza ritmo circadiano',
      },
    ],
    tags: ['sueño', 'ritual', 'salud', 'no-negociable'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: true,
    reminderTime: '22:00',
    emoji: '🌙',
  },

  {
    id: 'prot-work-close',
    title: 'Cierre Laboral (Trabajo Remoto)',
    description: 'Transición casa-noche para trabajo remoto',
    rationale:
      'En trabajo remoto, casa=trabajo=estudio=descanso genera fatiga invisible. El ritual de cierre protege el sistema nervioso y marca el fin del día laboral.',
    frequency: 'daily',
    layer: 'survival',
    priority: 'high',
    category: 'work',
    timeEstimate: 10,
    timeBlock: 'evening',
    idealTime: '18:30',
    subtasks: [
      {
        id: 'sub-close-checklist',
        label: 'Checklist de cierre: qué terminé, qué queda, primer paso mañana',
        isDone: false,
        order: 0,
        estimatedMinutes: 5,
        scientificNote: 'Efecto Zeigarnik: escribir pendientes libera memoria de trabajo',
      },
      {
        id: 'sub-close-computer',
        label: 'Apagar computador o cerrar apps de trabajo',
        isDone: false,
        order: 1,
        estimatedMinutes: 2,
        scientificNote: 'Barrera física = barrera psicológica. Trabajo remoto necesita rituales',
      },
      {
        id: 'sub-close-space',
        label: 'Guardar cuaderno/materiales de trabajo',
        isDone: false,
        order: 2,
        estimatedMinutes: 2,
        scientificNote: 'Espacio despejado = mente despejada para descanso',
      },
      {
        id: 'sub-close-verbal',
        label: 'Decir en voz alta: "Trabajo terminado"',
        isDone: false,
        order: 3,
        estimatedMinutes: 1,
        scientificNote: 'Verbalizar marca transición psicológica',
      },
    ],
    tags: ['trabajo', 'remoto', 'cierre', 'transición'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: true,
    reminderTime: '18:30',
    emoji: '🏠',
  },

  // ============================================
  // CAPA DE MANTENIMIENTO (MAINTENANCE)
  // ============================================

  {
    id: 'prot-deepwork',
    title: 'Bloque de Producción (Deep Work)',
    description: 'Trabajo enfocado en lo más importante',
    rationale:
      'Cal Newport: 1 hora de trabajo profundo > 4 horas de trabajo fragmentado. El cerebro TDAH puede hiperfocalizar si elimina distracciones y tiene meta clara.',
    frequency: 'daily',
    layer: 'maintenance',
    priority: 'high',
    category: 'work',
    timeEstimate: 50,
    timeBlock: 'morning',
    idealTime: '09:00',
    subtasks: [
      {
        id: 'sub-prep',
        label: 'Preparar entorno (agua, snack, siguiente paso visible)',
        isDone: false,
        order: 0,
        estimatedMinutes: 3,
        scientificNote: 'Reducir fricciones antes de empezar. No levantarse durante el bloque',
      },
      {
        id: 'sub-define',
        label: 'Definir qué significa "terminar" este bloque',
        isDone: false,
        order: 1,
        estimatedMinutes: 2,
        scientificNote: 'Meta observable: commit, envío, ejercicio resuelto',
      },
      {
        id: 'sub-block',
        label: 'Bloque de foco (25-50 min)',
        isDone: false,
        order: 2,
        estimatedMinutes: 50,
        scientificNote: 'Pomodoro adaptado: 25-50min según estado. TDAH puede hiperfocalizar si elimina distracciones',
      },
      {
        id: 'sub-evidence',
        label: 'Producir 1 entrega verificable',
        isDone: false,
        order: 3,
        estimatedMinutes: 5,
        scientificNote: 'Evidencia tangible reduce ansiedad',
      },
    ],
    tags: ['trabajo', 'foco', 'productividad', 'laboral'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: true,
    reminderTime: '10:00',
    emoji: '🧠',
  },

  {
    id: 'prot-active-break-am',
    title: 'Pausa Activa (Mañana)',
    description: 'Interrupción breve para moverse y rehidratarse',
    rationale:
      'Las pausas activas cada 45-60 min reducen riesgo en trabajo sedentario (OMS). Previenen fatiga, mejoran circulación y resetean la atención. Para TDAH: la pausa estructurada es mejor que "cuando me acuerde".',
    frequency: 'daily',
    layer: 'maintenance',
    priority: 'high',
    category: 'health',
    timeEstimate: 5,
    timeBlock: 'morning',
    idealTime: '10:30',
    subtasks: [
      {
        id: 'sub-water-break',
        label: 'Beber agua',
        isDone: false,
        order: 0,
        estimatedMinutes: 1,
        scientificNote: 'Hidratación constante mejora concentración y reduce fatiga cognitiva',
      },
      {
        id: 'sub-posture',
        label: 'Corregir postura (hombros atrás, pantalla a altura de ojos)',
        isDone: false,
        order: 1,
        estimatedMinutes: 1,
        scientificNote: 'Postura correcta reduce dolor cervical y lumbar por trabajo sedentario (OSHA)',
      },
      {
        id: 'sub-stretch',
        label: 'Estiramientos: cuello, espalda, muñecas (1-2 min)',
        isDone: false,
        order: 2,
        estimatedMinutes: 2,
        scientificNote: 'Previene tensión por postura estática',
      },
    ],
    tags: ['pausa', 'movimiento', 'trabajo', 'laboral'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: true,
    reminderTime: '10:30',
    emoji: '🧘',
  },

  {
    id: 'prot-micro-reset',
    title: 'Micro-Reset Mediodía',
    description: 'Pausa breve de respiración o estiramiento',
    rationale:
      'El mediodía es punto de inflexión. Un micro-reset de 10 min de respiración o estiramiento (sin redes sociales) previene el bajón de la tarde.',
    frequency: 'daily',
    layer: 'maintenance',
    priority: 'medium',
    category: 'self-care',
    timeEstimate: 10,
    timeBlock: 'afternoon',
    idealTime: '12:00',
    subtasks: [
      {
        id: 'sub-breathe-noon',
        label: 'Respiración profunda 3-5 min',
        isDone: false,
        order: 0,
        estimatedMinutes: 5,
        scientificNote: 'No redes. Respiración lenta reduce cortisol.',
      },
      {
        id: 'sub-stretch-noon',
        label: 'Estiramiento de pie o caminata corta',
        isDone: false,
        order: 1,
        estimatedMinutes: 5,
        scientificNote: 'Movimiento activa circulación y reduce rigidez muscular por sedentarismo',
      },
    ],
    tags: ['pausa', 'respiración', 'trabajo', 'laboral'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: true,
    reminderTime: '12:00',
    emoji: '🌬️',
  },

  {
    id: 'prot-active-break-pm',
    title: 'Pausa Activa (Tarde)',
    description: 'Interrupción breve antes del cierre laboral',
    rationale:
      'Segunda pausa activa del día. Las 16:30 es punto de fatiga típico. Agua + colación + estiramiento previene el colapso de energía final.',
    frequency: 'daily',
    layer: 'maintenance',
    priority: 'high',
    category: 'health',
    timeEstimate: 10,
    timeBlock: 'afternoon',
    idealTime: '16:30',
    subtasks: [
      {
        id: 'sub-water-pm',
        label: 'Beber agua',
        isDone: false,
        order: 0,
        estimatedMinutes: 1,
        scientificNote: 'El bajón vespertino se intensifica con deshidratación',
      },
      {
        id: 'sub-snack-pm',
        label: 'Colación ligera si hay hambre',
        isDone: false,
        order: 1,
        estimatedMinutes: 5,
        scientificNote: 'Fruta, frutos secos, hummus',
      },
      {
        id: 'sub-stretch-pm',
        label: 'Estiramiento: espalda, piernas, ojos (mirar lejos)',
        isDone: false,
        order: 2,
        estimatedMinutes: 3,
        scientificNote: 'Regla 20-20-20: cada 20 min, mirar 20 pies de distancia por 20 seg',
      },
    ],
    tags: ['pausa', 'movimiento', 'trabajo', 'laboral'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: true,
    reminderTime: '16:30',
    emoji: '🧘',
  },

  {
    id: 'prot-home-reset',
    title: 'Reset de Entropía Hogar',
    description: 'Evitar que el caos ambiental drene energía',
    rationale:
      'El desorden visual consume recursos cognitivos limitados. Un reset diario de 10-15 min previene la acumulación que paraliza. La casa que "no grita" libera ancho de banda mental.',
    frequency: 'daily',
    layer: 'maintenance',
    priority: 'high',
    category: 'home',
    timeEstimate: 15,
    timeBlock: 'evening',
    idealTime: '19:00',
    subtasks: [
      {
        id: 'sub-kitchen',
        label: 'Cocina: loza crítica + superficies',
        isDone: false,
        order: 0,
        estimatedMinutes: 5,
        scientificNote: 'Superficies limpias reducen carga bacteriana y mental',
      },
      {
        id: 'sub-trash',
        label: 'Basura (si aplica)',
        isDone: false,
        order: 1,
        estimatedMinutes: 2,
        scientificNote: 'Basura acumulada atrae insectos y genera olores que afectan bienestar',
      },
      {
        id: 'sub-reset',
        label: 'Reset 10 min: recoger visible, devolver cosas a su lugar',
        isDone: false,
        order: 2,
        estimatedMinutes: 10,
        scientificNote: 'El desorden visual consume recursos cognitivos limitados (Princeton 2011)',
      },
    ],
    tags: ['hogar', 'orden', 'limpieza'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '🏠',
  },

  {
    id: 'prot-food',
    title: 'Decisión de Comida',
    description: 'Reducir fricción de cocina',
    rationale:
      'Decidir qué cocinar consume energía. Hacerlo temprano, antes del cansancio, elimina la parálisis de la pregunta "¿qué como?".',
    frequency: 'daily',
    layer: 'maintenance',
    priority: 'medium',
    category: 'health',
    timeEstimate: 5,
    timeBlock: 'morning',
    idealTime: '10:30',
    subtasks: [
      {
        id: 'sub-decide',
        label: 'Decidir qué se cocina HOY',
        isDone: false,
        order: 0,
        estimatedMinutes: 2,
        scientificNote: 'Antes de que aparezca el cansancio',
      },
      {
        id: 'sub-prep-food',
        label: 'Adelantar algo (lavar, porcionar, remojar)',
        isDone: false,
        order: 1,
        estimatedMinutes: 5,
        scientificNote: 'Reducir fricción = mayor adherencia a alimentación saludable',
      },
    ],
    tags: ['comida', 'planificación'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '🍳',
  },

  {
    id: 'prot-pets-play',
    title: 'Tiempo de Juego (Mascotas)',
    description: 'Vínculo y regulación de energía felina',
    rationale:
      'El juego estructurado previene comportamientos por aburrimiento, fortalece el vínculo y regula el ciclo de energía (especialmente antes del anochecer).',
    frequency: 'daily',
    layer: 'maintenance',
    priority: 'medium',
    category: 'pets',
    timeEstimate: 15,
    timeBlock: 'evening',
    idealTime: '19:15',
    subtasks: [
      {
        id: 'sub-play',
        label: 'Juego activo 10-15 min',
        isDone: false,
        order: 0,
        estimatedMinutes: 12,
        scientificNote: 'Ideal antes del anochecer',
      },
      {
        id: 'sub-observe',
        label: 'Observar nivel de energía/respuesta',
        isDone: false,
        order: 1,
        estimatedMinutes: 3,
        scientificNote: 'Cambios en comportamiento pueden indicar problemas de salud',
      },
    ],
    tags: ['mascotas', 'juego', 'vínculo'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: true,
    reminderTime: '18:00',
    emoji: '🎾',
  },

  {
    id: 'prot-relationship',
    title: 'Check-in Relacional',
    description: 'Mantenimiento del vínculo con Cami',
    rationale:
      'Las relaciones requieren atención regular. Un check-in breve previene acumulación de distancia emocional y malentendidos.',
    frequency: 'daily',
    layer: 'maintenance',
    priority: 'medium',
    category: 'social',
    timeEstimate: 5,
    timeBlock: 'evening',
    idealTime: '19:30',
    subtasks: [
      {
        id: 'sub-checkin',
        label: '¿Cómo estás hoy? + ¿Necesitas algo concreto?',
        isDone: false,
        order: 0,
        estimatedMinutes: 2,
        scientificNote: 'Preguntas específicas generan conversaciones más significativas (Gottman)',
      },
      {
        id: 'sub-gesture',
        label: 'Un gesto pequeño (té, orden, compañía silenciosa)',
        isDone: false,
        order: 1,
        estimatedMinutes: 3,
        scientificNote: 'Los pequeños gestos diarios mantienen el "banco emocional" positivo (Gottman)',
      },
      {
        id: 'sub-tension',
        label: 'Si hay tensión: acordar momento para hablar (no ahora exhausta)',
        isDone: false,
        order: 2,
        estimatedMinutes: 2,
        scientificNote: 'Evitar conversaciones difusas por cansancio',
      },
    ],
    tags: ['relación', 'convivencia'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '💕',
  },

  {
    id: 'prot-expenses',
    title: 'Registro de Gastos',
    description: 'Control diario de gastos',
    rationale:
      'Los gastos pequeños se acumulan invisiblemente. Registrar diariamente toma 2 minutos y previene sorpresas financieras al final del mes.',
    frequency: 'daily',
    layer: 'maintenance',
    priority: 'low',
    category: 'finance',
    timeEstimate: 2,
    timeBlock: 'evening',
    idealTime: '21:00',
    subtasks: [
      {
        id: 'sub-log-expenses',
        label: 'Registrar gastos del día (app/nota)',
        isDone: false,
        order: 0,
        estimatedMinutes: 2,
        scientificNote: 'Registrar gastos aumenta consciencia financiera y reduce gasto impulsivo',
      },
    ],
    tags: ['finanzas', 'gastos', 'control'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '💳',
  },

  // ============================================
  // CAPA DE CRECIMIENTO (GROWTH)
  // ============================================

  {
    id: 'prot-study',
    title: 'Capital Intelectual',
    description: 'Estudio con continuidad',
    rationale:
      'La neuroplasticidad se construye con consistencia, no con maratones. 20-30 min diarios superan 4h esporádicas. El cierre con pregunta fija conocimiento.',
    frequency: 'daily',
    layer: 'growth',
    priority: 'high',
    category: 'study',
    timeEstimate: 30,
    timeBlock: 'evening',
    idealTime: '20:30',
    subtasks: [
      {
        id: 'sub-study-block',
        label: 'Bloque de estudio (20-30 min)',
        isDone: false,
        order: 0,
        estimatedMinutes: 30,
        scientificNote: 'Espaciado > maratón. 20-30 min diarios superan 4h esporádicas en retención',
      },
      {
        id: 'sub-question',
        label: '¿Qué entendí? ¿Qué no?',
        isDone: false,
        order: 1,
        estimatedMinutes: 3,
        scientificNote: 'Metacognición: evaluar el propio aprendizaje',
      },
      {
        id: 'sub-doubt',
        label: 'Registrar 1 duda concreta para resolver',
        isDone: false,
        order: 2,
        estimatedMinutes: 2,
        scientificNote: 'Evita espiral de búsqueda infinita',
      },
    ],
    tags: ['estudio', 'aprendizaje'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '📚',
  },

  {
    id: 'prot-movement',
    title: 'Movimiento Básico',
    description: 'Activación física mínima',
    rationale:
      'El ejercicio mejora función ejecutiva y regulación emocional. 10-20 min de movimiento suave activan circuitos que el sedentarismo apaga. Nota asma: calentamiento suave 5 min antes.',
    frequency: 'daily',
    layer: 'growth',
    priority: 'medium',
    category: 'health',
    timeEstimate: 15,
    timeBlock: 'morning',
    idealTime: '08:00',
    subtasks: [
      {
        id: 'sub-warmup',
        label: 'Calentamiento suave 5 min (importante por asma)',
        isDone: false,
        order: 0,
        estimatedMinutes: 5,
        scientificNote: 'El calentamiento gradual previene broncoespasmo por ejercicio',
      },
      {
        id: 'sub-move',
        label: '10-20 min de movimiento (caminar, movilidad, TRX)',
        isDone: false,
        order: 1,
        estimatedMinutes: 15,
        scientificNote: 'OMS: 150-300 min/semana actividad moderada. 20 min/día mejora función ejecutiva',
      },
    ],
    tags: ['ejercicio', 'movimiento', 'salud'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '🏃‍♀️',
  },

  {
    id: 'prot-mindfulness',
    title: 'Mindfulness Breve',
    description: 'Meditación corta para regulación atencional',
    rationale:
      'Estudios muestran que mindfulness mejora atención, control inhibitorio y regulación emocional en TDAH (efecto moderado-grande: d=0.55-0.86). 10 min diarios durante 8 semanas mostraron mejora en foco y menor ansiedad (UCLA). Se recomienda práctica guiada corta, no silencio prolongado.',
    frequency: 'daily',
    layer: 'growth',
    priority: 'medium',
    category: 'self-care',
    timeEstimate: 10,
    timeBlock: 'morning',
    idealTime: '08:20',
    subtasks: [
      {
        id: 'sub-breathe',
        label: 'Respiración consciente (3-5 min)',
        isDone: false,
        order: 0,
        estimatedMinutes: 5,
        scientificNote: 'Enfocar atención en respiración, notar distracción, volver',
      },
      {
        id: 'sub-body-scan',
        label: 'Escaneo corporal breve o meditación guiada (5 min)',
        isDone: false,
        order: 1,
        estimatedMinutes: 5,
        scientificNote: 'Apps: Headspace, Calm, Insight Timer. Guiado > silencio para TDAH',
      },
    ],
    tags: ['mindfulness', 'meditación', 'TDAH', 'atención'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '🧘',
  },

  {
    id: 'prot-close',
    title: 'Cierre del Día',
    description: 'Cerrar ciclos y preparar mañana',
    rationale:
      'El cierre reduce la rumiación nocturna. Preparar el primer paso de mañana elimina la fricción de arranque. El cerebro descansa mejor sin "asuntos pendientes".',
    frequency: 'daily',
    layer: 'growth',
    priority: 'medium',
    category: 'self-care',
    timeEstimate: 10,
    timeBlock: 'evening',
    idealTime: '21:30',
    subtasks: [
      {
        id: 'sub-review',
        label: 'Qué salió bien (aunque sea pequeño)',
        isDone: false,
        order: 0,
        estimatedMinutes: 2,
        scientificNote: 'Reconocer logros pequeños reduce autocrítica y mejora motivación',
      },
      {
        id: 'sub-open',
        label: 'Qué quedó abierto',
        isDone: false,
        order: 1,
        estimatedMinutes: 2,
        scientificNote: 'Efecto Zeigarnik: escribir pendientes libera memoria de trabajo',
      },
      {
        id: 'sub-next',
        label: 'Primer paso de mañana (documento, tarea, ropa)',
        isDone: false,
        order: 2,
        estimatedMinutes: 3,
        scientificNote: 'Reduce fricción de arranque',
      },
      {
        id: 'sub-calendar',
        label: 'Revisar calendario del día siguiente',
        isDone: false,
        order: 3,
        estimatedMinutes: 2,
        scientificNote: 'Anticipar el día reduce ansiedad matutina y mejora preparación',
      },
    ],
    tags: ['cierre', 'planificación', 'noche'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: true,
    reminderTime: '21:30',
    emoji: '🌙',
  },

  // ============================================
  // PROTOCOLOS FIN DE SEMANA (horarios +1h)
  // Basado en evidencia: máximo 1h de diferencia para evitar jet lag social
  // ============================================

  {
    id: 'prot-bio-vital-weekend',
    title: 'Soporte Vital (Finde)',
    description: 'Funciones básicas - horario flexible de fin de semana',
    rationale:
      'El jet lag social (dormir diferente el finde) afecta salud metabólica. Máximo 1h de diferencia mantiene el ritmo circadiano. La medicación sigue siendo crítica.',
    frequency: 'daily',
    layer: 'survival',
    priority: 'critical',
    category: 'health',
    timeEstimate: 10,
    timeBlock: 'morning',
    idealTime: '08:30',
    subtasks: [
      {
        id: 'sub-med-we',
        label: 'Tomar Escitalopram 20mg (08:00-08:30)',
        isDone: false,
        order: 0,
        estimatedMinutes: 1,
        isCritical: true,
        scientificNote: 'Medicación matutina - máximo 1h después de lo habitual',
      },
      {
        id: 'sub-water-1-we',
        label: 'Primer vaso grande de agua',
        isDone: false,
        order: 1,
        estimatedMinutes: 1,
        scientificNote: 'Rehidratar tras el sueño',
      },
      {
        id: 'sub-water-2-we',
        label: 'Segundo vaso de agua (antes de mediodía)',
        isDone: false,
        order: 2,
        estimatedMinutes: 1,
        scientificNote: 'Mantener hidratación constante incluso los fines de semana',
      },
      {
        id: 'sub-light-we',
        label: 'Exposición a luz natural (3-10 min)',
        isDone: false,
        order: 3,
        estimatedMinutes: 5,
        scientificNote: 'Mantiene sincronizado el reloj biológico',
      },
    ],
    tags: ['salud', 'medicación', 'hidratación', 'finde'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: true,
    reminderTime: '08:30',
    emoji: '💊',
  },

  {
    id: 'prot-breakfast-weekend',
    title: 'Desayuno Vegano (Finde)',
    description: 'Primera comida - horario relajado de fin de semana',
    rationale:
      'El desayuno puede retrasarse máximo 1h sin afectar significativamente el metabolismo. Ventana de alimentación se ajusta: 8:30-21:00.',
    frequency: 'daily',
    layer: 'survival',
    priority: 'high',
    category: 'health',
    timeEstimate: 25,
    timeBlock: 'morning',
    idealTime: '08:30',
    subtasks: [
      {
        id: 'sub-breakfast-prep-we',
        label: 'Preparar desayuno con calma',
        isDone: false,
        order: 0,
        estimatedMinutes: 15,
        scientificNote: 'Finde = tiempo para cocinar algo más elaborado',
      },
      {
        id: 'sub-breakfast-eat-we',
        label: 'Desayunar sin prisa (sin pantallas)',
        isDone: false,
        order: 1,
        estimatedMinutes: 10,
        isCritical: true,
        scientificNote: 'Proteína + fibra: pancakes veganos, tofu revuelto, smoothie bowl',
      },
    ],
    tags: ['alimentación', 'crononutrición', 'vegano', 'mañana', 'finde'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: true,
    reminderTime: '08:30',
    emoji: '🥞',
  },

  {
    id: 'prot-pets-weekend',
    title: 'Philippa & Fjor (Finde)',
    description: 'Cuidado de gatos - horario de fin de semana',
    rationale:
      'Los gatos también se benefician de rutinas consistentes, pero pueden esperar un poco más el finde. Su alimentación es automática por dispensador.',
    frequency: 'daily',
    layer: 'survival',
    priority: 'critical',
    category: 'pets',
    timeEstimate: 15,
    timeBlock: 'morning',
    idealTime: '08:45',
    subtasks: [
      {
        id: 'sub-litter-we',
        label: 'Limpiar areneros',
        isDone: false,
        order: 0,
        estimatedMinutes: 5,
        isCritical: true,
        scientificNote: 'No negociable: gatos evitan areneros sucios, causando retención urinaria',
      },
      {
        id: 'sub-water-pets-we',
        label: 'Agua fresca disponible',
        isDone: false,
        order: 1,
        estimatedMinutes: 2,
        scientificNote: 'Cambiar agua diariamente previene crecimiento bacteriano',
      },
      {
        id: 'sub-check-pets-we',
        label: 'Chequeo visual (ojos, nariz, apetito)',
        isDone: false,
        order: 2,
        estimatedMinutes: 2,
        scientificNote: 'Detectar cambios temprano evita emergencias veterinarias',
      },
    ],
    tags: ['gatos', 'mascotas', 'cuidado', 'finde'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: true,
    reminderTime: '08:45',
    emoji: '🐱',
  },

  // ============================================
  // PROTOCOLOS SEMANALES
  // ============================================

  {
    id: 'prot-weekly-review',
    title: 'Auditoría Semanal',
    description: 'Revisión y ajuste del sistema',
    rationale:
      'Sin revisión, los sistemas se degradan. 30-45 min semanales para evaluar qué funciona evita semanas de deriva acumulada.',
    frequency: 'weekly',
    layer: 'growth',
    priority: 'high',
    category: 'self-care',
    timeEstimate: 45,
    subtasks: [
      {
        id: 'sub-what-worked',
        label: 'Qué funcionó esta semana',
        isDone: false,
        order: 0,
        estimatedMinutes: 5,
        scientificNote: 'Identificar éxitos permite replicarlos conscientemente',
      },
      {
        id: 'sub-what-failed',
        label: 'Qué falló (sin juicio)',
        isDone: false,
        order: 1,
        estimatedMinutes: 5,
        scientificNote: 'Auto-compasión > auto-crítica para cambio sostenible (Neff 2011)',
      },
      {
        id: 'sub-adjust',
        label: '1 ajuste concreto para la semana',
        isDone: false,
        order: 2,
        estimatedMinutes: 5,
        scientificNote: 'Cambios pequeños y específicos tienen mayor adherencia',
      },
      {
        id: 'sub-top3-week',
        label: 'Top 3 resultados de la semana',
        isDone: false,
        order: 3,
        estimatedMinutes: 5,
        scientificNote: 'Celebrar logros aumenta motivación intrínseca',
      },
      {
        id: 'sub-notes',
        label: 'Ordenar notas (OneNote/Notion) - 20 min',
        isDone: false,
        order: 4,
        estimatedMinutes: 20,
        scientificNote: 'Notas organizadas reducen carga cognitiva de búsqueda',
      },
    ],
    tags: ['revisión', 'planificación', 'ajuste'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: true,
    reminderTime: '10:00',
    emoji: '📊',
  },

  {
    id: 'prot-deep-clean',
    title: 'Limpieza Profunda Hogar',
    description: 'Mantenimiento semanal del hogar',
    rationale:
      'La limpieza profunda semanal previene acumulación que requeriría horas de trabajo intenso. Mejor 1h semanal que 6h de emergencia.',
    frequency: 'weekly',
    layer: 'maintenance',
    priority: 'high',
    category: 'home',
    timeEstimate: 60,
    subtasks: [
      {
        id: 'sub-bathroom',
        label: 'Baño: limpieza completa',
        isDone: false,
        order: 0,
        estimatedMinutes: 15,
        scientificNote: 'Baños son fuentes de bacterias; limpieza semanal reduce riesgo de infecciones',
      },
      {
        id: 'sub-floors',
        label: 'Aspirar/barrer + trapear',
        isDone: false,
        order: 1,
        estimatedMinutes: 15,
        scientificNote: 'Reduce alérgenos, pelo de mascotas y polvo acumulado',
      },
      {
        id: 'sub-sheets',
        label: 'Sábanas/fundas',
        isDone: false,
        order: 2,
        estimatedMinutes: 10,
        scientificNote: 'Cambiar sábanas semanalmente reduce ácaros y mejora calidad de sueño',
      },
      {
        id: 'sub-laundry',
        label: 'Lavandería: ropa + toallas',
        isDone: false,
        order: 3,
        estimatedMinutes: 10,
        scientificNote: 'Toallas húmedas acumulan bacterias; lavar cada 3-4 usos',
      },
      {
        id: 'sub-litter-deep',
        label: 'Zona de areneros: limpieza alrededor',
        isDone: false,
        order: 4,
        estimatedMinutes: 5,
        scientificNote: 'Arena se esparce; limpiar evita contaminación de otras áreas',
      },
      {
        id: 'sub-fridge',
        label: 'Refrigerador: revisión sobras/caducidades',
        isDone: false,
        order: 5,
        estimatedMinutes: 5,
        scientificNote: 'Eliminar sobras >4 días previene intoxicación alimentaria (FDA)',
      },
    ],
    tags: ['limpieza', 'hogar', 'mantenimiento'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '🧹',
  },

  {
    id: 'prot-finance-weekly',
    title: 'Finanzas Semanales',
    description: 'Control de gastos y presupuesto',
    rationale:
      'El descontrol financiero genera ansiedad de fondo. Una revisión semanal de 10 min previene sorpresas y mantiene el control.',
    frequency: 'weekly',
    layer: 'maintenance',
    priority: 'medium',
    category: 'finance',
    timeEstimate: 15,
    subtasks: [
      {
        id: 'sub-expenses',
        label: 'Conciliar gastos (banco/app/notas)',
        isDone: false,
        order: 0,
        estimatedMinutes: 5,
        scientificNote: 'Detectar discrepancias temprano evita problemas mayores',
      },
      {
        id: 'sub-subs',
        label: 'Revisar suscripciones y pagos próximos',
        isDone: false,
        order: 1,
        estimatedMinutes: 5,
        scientificNote: 'Suscripciones olvidadas son gastos hormiga significativos',
      },
      {
        id: 'sub-limit',
        label: 'Definir tope de gasto semanal',
        isDone: false,
        order: 2,
        estimatedMinutes: 3,
        scientificNote: 'Límites claros reducen gasto impulsivo',
      },
    ],
    tags: ['finanzas', 'presupuesto', 'gastos'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '💰',
  },

  {
    id: 'prot-tech-check',
    title: 'Auditoría de Sistemas',
    description: 'Revisión de VPS, n8n y automatizaciones',
    rationale:
      'Los sistemas técnicos requieren mantenimiento. Una revisión semanal de 15 min previene caídas inesperadas y mantiene trazabilidad.',
    frequency: 'weekly',
    layer: 'maintenance',
    priority: 'medium',
    category: 'tech',
    timeEstimate: 15,
    subtasks: [
      {
        id: 'sub-logs',
        label: 'Revisar logs/alertas del stack',
        isDone: false,
        order: 0,
        estimatedMinutes: 5,
        scientificNote: 'Detectar anomalías antes de que se conviertan en caídas',
      },
      {
        id: 'sub-auto',
        label: 'Verificar automatizaciones críticas',
        isDone: false,
        order: 1,
        estimatedMinutes: 5,
        scientificNote: 'Las automatizaciones pueden fallar silenciosamente',
      },
      {
        id: 'sub-note-tech',
        label: 'Anotar qué se cambió y por qué (bitácora)',
        isDone: false,
        order: 2,
        estimatedMinutes: 5,
        scientificNote: 'Documentación previene repetir errores y facilita debug',
      },
    ],
    tags: ['tecnología', 'vps', 'n8n', 'sistemas'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '🖥️',
  },

  {
    id: 'prot-restock',
    title: 'Reposición de Insumos',
    description: 'Verificar y reponer lo que se agota',
    rationale:
      'Quedarse sin insumos básicos genera estrés innecesario. Una revisión semanal previene emergencias de "se acabó el papel".',
    frequency: 'weekly',
    layer: 'maintenance',
    priority: 'medium',
    category: 'home',
    timeEstimate: 10,
    subtasks: [
      {
        id: 'sub-toilet',
        label: 'Papel higiénico/pañuelos',
        isDone: false,
        order: 0,
        estimatedMinutes: 2,
        scientificNote: 'Evitar emergencias de "se acabó" reduce estrés innecesario',
      },
      {
        id: 'sub-cleaning',
        label: 'Productos de limpieza',
        isDone: false,
        order: 1,
        estimatedMinutes: 2,
        scientificNote: 'Mantener stock mínimo permite limpiar cuando toca',
      },
      {
        id: 'sub-pet-supplies',
        label: 'Arena y comida de mascotas',
        isDone: false,
        order: 2,
        estimatedMinutes: 3,
        isCritical: true,
        scientificNote: 'Dependientes no pueden esperar; siempre tener reserva',
      },
      {
        id: 'sub-personal',
        label: 'Higiene personal (jabón, shampoo, etc.)',
        isDone: false,
        order: 3,
        estimatedMinutes: 2,
        scientificNote: 'Comprar antes de acabarse reduce fricción de rutina',
      },
    ],
    tags: ['reposición', 'hogar', 'compras'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '🛒',
  },

  {
    id: 'prot-pets-weekly',
    title: 'Cuidado Profundo Mascotas',
    description: 'Mantenimiento semanal de Philippa y Fjor',
    rationale:
      'El cuidado diario no es suficiente. Semanalmente se necesita limpieza más profunda de sus cosas y observación más detallada.',
    frequency: 'weekly',
    layer: 'maintenance',
    priority: 'high',
    category: 'pets',
    timeEstimate: 30,
    subtasks: [
      {
        id: 'sub-bowls-deep',
        label: 'Lavado profundo de platos/comederos/bebederos',
        isDone: false,
        order: 0,
        estimatedMinutes: 5,
        scientificNote: 'Biofilm bacteriano se forma en comederos; lavar con jabón elimina',
      },
      {
        id: 'sub-litter-area',
        label: 'Limpieza del área alrededor de areneros',
        isDone: false,
        order: 1,
        estimatedMinutes: 5,
        scientificNote: 'Reduce olores y contaminación cruzada en el hogar',
      },
      {
        id: 'sub-weight-obs',
        label: 'Observar peso/apetito/actividad y anotar cambios',
        isDone: false,
        order: 2,
        estimatedMinutes: 3,
        scientificNote: 'Cambios graduales difíciles de notar; registro ayuda a detectarlos',
      },
      {
        id: 'sub-play-long',
        label: 'Sesión de juego más larga (20-30 min)',
        isDone: false,
        order: 3,
        estimatedMinutes: 25,
        scientificNote: 'El juego intenso previene obesidad y problemas de comportamiento',
      },
    ],
    tags: ['mascotas', 'cuidado', 'limpieza'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '🐱',
  },

  {
    id: 'prot-study-prep',
    title: 'Preparación de Estudio',
    description: 'Planificar qué estudiar la próxima semana',
    rationale:
      'Estudiar sin plan lleva a dispersión. Saber qué unidades, qué evaluaciones vienen y qué prácticas hacer reduce la fricción de arranque.',
    frequency: 'weekly',
    layer: 'growth',
    priority: 'high',
    category: 'study',
    timeEstimate: 20,
    subtasks: [
      {
        id: 'sub-units',
        label: 'Revisar qué unidades/temas tocan esta semana',
        isDone: false,
        order: 0,
        estimatedMinutes: 5,
        scientificNote: 'Saber qué viene reduce ansiedad y permite preparación',
      },
      {
        id: 'sub-evals',
        label: 'Verificar evaluaciones/entregas próximas',
        isDone: false,
        order: 1,
        estimatedMinutes: 5,
        scientificNote: 'Evitar sorpresas de última hora reduce estrés académico',
      },
      {
        id: 'sub-practice',
        label: 'Definir prácticas mínimas (ejercicios, lecturas)',
        isDone: false,
        order: 2,
        estimatedMinutes: 5,
        scientificNote: 'Práctica distribuida > maratones de estudio (spacing effect)',
      },
    ],
    tags: ['estudio', 'planificación', 'universidad'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '📖',
  },

  {
    id: 'prot-work-backlog',
    title: 'Backlog de Mejoras',
    description: 'Una mejora técnica o profesional pequeña',
    rationale:
      'Las mejoras pequeñas acumuladas generan crecimiento sostenido. Una mejora por semana = 52 mejoras al año.',
    frequency: 'weekly',
    layer: 'growth',
    priority: 'medium',
    category: 'work',
    timeEstimate: 45,
    subtasks: [
      {
        id: 'sub-choose',
        label: 'Elegir 1 mejora (documentación, refactor, aprendizaje)',
        isDone: false,
        order: 0,
        estimatedMinutes: 5,
        scientificNote: 'Mejoras incrementales acumulan valor significativo (kaizen)',
      },
      {
        id: 'sub-execute',
        label: 'Ejecutar la mejora (30-45 min)',
        isDone: false,
        order: 1,
        estimatedMinutes: 45,
        scientificNote: 'Timeboxing previene perfeccionismo paralizante',
      },
      {
        id: 'sub-document',
        label: 'Documentar qué se hizo (para no repetir)',
        isDone: false,
        order: 2,
        estimatedMinutes: 5,
        scientificNote: 'Documentar externaliza memoria y ahorra tiempo futuro',
      },
    ],
    tags: ['trabajo', 'mejora', 'técnico'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '⚡',
  },

  {
    id: 'prot-portfolio',
    title: 'Trabajo en Portafolio',
    description: 'Avanzar el portafolio profesional',
    rationale:
      'El portafolio es evidencia de capacidad. 30-60 min semanales construyen presencia profesional sin agotarse.',
    frequency: 'weekly',
    layer: 'growth',
    priority: 'medium',
    category: 'work',
    timeEstimate: 45,
    subtasks: [
      {
        id: 'sub-portfolio-time',
        label: 'Trabajar en portafolio (30-60 min)',
        isDone: false,
        order: 0,
        estimatedMinutes: 45,
        scientificNote: 'Portafolio es evidencia tangible de capacidad profesional',
      },
      {
        id: 'sub-portfolio-note',
        label: 'Anotar qué falta para la próxima sesión',
        isDone: false,
        order: 1,
        estimatedMinutes: 3,
        scientificNote: 'Efecto Zeigarnik: anotar siguiente paso facilita retomar',
      },
    ],
    tags: ['portafolio', 'carrera', 'profesional'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '💼',
  },

  {
    id: 'prot-movement-plan',
    title: 'Plan de Movimiento Semanal',
    description: 'Planificar sesiones de ejercicio',
    rationale:
      'El ejercicio planificado se hace. Sin plan, el "haré ejercicio" se posterga indefinidamente. 2-4 sesiones por semana es sostenible.',
    frequency: 'weekly',
    layer: 'growth',
    priority: 'medium',
    category: 'health',
    timeEstimate: 10,
    subtasks: [
      {
        id: 'sub-plan-sessions',
        label: 'Definir 2-4 sesiones de movimiento para la semana',
        isDone: false,
        order: 0,
        estimatedMinutes: 3,
        scientificNote: 'Intención de implementación aumenta adherencia al ejercicio 2-3x',
      },
      {
        id: 'sub-plan-type',
        label: 'Tipo de movimiento (caminar, TRX, yoga, etc.)',
        isDone: false,
        order: 1,
        estimatedMinutes: 3,
        scientificNote: 'Variedad previene aburrimiento y trabaja diferentes sistemas',
      },
      {
        id: 'sub-adherence',
        label: 'Revisar adherencia de la semana pasada (sin juicio)',
        isDone: false,
        order: 2,
        estimatedMinutes: 3,
        scientificNote: 'Auto-monitoreo sin juicio mejora consistencia a largo plazo',
      },
    ],
    tags: ['ejercicio', 'planificación', 'salud'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '🏋️',
  },

  {
    id: 'prot-social-contact',
    title: 'Contacto Social Intencional',
    description: 'Mantener red de relaciones',
    rationale:
      'Las relaciones se atrofian sin mantenimiento. Un contacto semanal intencional previene el aislamiento sin ser agotador.',
    frequency: 'weekly',
    layer: 'growth',
    priority: 'low',
    category: 'social',
    timeEstimate: 15,
    subtasks: [
      {
        id: 'sub-choose-person',
        label: 'Elegir 1 persona relevante (familia/amigo/red)',
        isDone: false,
        order: 0,
        estimatedMinutes: 2,
        scientificNote: 'Conexiones sociales fuertes asociadas a mayor longevidad (Harvard 2023)',
      },
      {
        id: 'sub-contact',
        label: 'Contactar con intención concreta (no solo "hola")',
        isDone: false,
        order: 1,
        estimatedMinutes: 10,
        scientificNote: 'Ej: "¿Cómo va tu proyecto?", "Vi esto y pensé en ti"',
      },
    ],
    tags: ['social', 'relaciones', 'red'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '👋',
  },

  {
    id: 'prot-couple-meeting',
    title: 'Reunión de Pareja (State of the Union)',
    description: 'Reunión semanal logística y emocional con Cami',
    rationale:
      'Formato "State of the Union" de Gottman: reunión estructurada previene acumulación de conflicto. El guión fijo mantiene foco en la semana, no en terapia.',
    frequency: 'weekly',
    layer: 'maintenance',
    priority: 'high',
    category: 'social',
    timeEstimate: 45,
    subtasks: [
      {
        id: 'sub-couple-good',
        label: '¿Qué funcionó esta semana?',
        isDone: false,
        order: 0,
        estimatedMinutes: 5,
        scientificNote: 'Empezar por lo positivo crea ambiente seguro para lo difícil',
      },
      {
        id: 'sub-couple-hard',
        label: '¿Qué fue difícil o costó?',
        isDone: false,
        order: 1,
        estimatedMinutes: 10,
        scientificNote: 'Expresar dificultades previene acumulación de resentimiento',
      },
      {
        id: 'sub-couple-gesture',
        label: '¿Qué gesto del otro ayudó?',
        isDone: false,
        order: 2,
        estimatedMinutes: 5,
        scientificNote: 'Expresar gratitud concreta aumenta satisfacción',
      },
      {
        id: 'sub-couple-agreements',
        label: 'Acuerdos concretos para la próxima semana',
        isDone: false,
        order: 3,
        estimatedMinutes: 10,
        scientificNote: 'Acuerdos específicos > intenciones vagas para cambio real',
      },
      {
        id: 'sub-couple-board',
        label: 'Revisar tablero de responsabilidades (quién hace qué)',
        isDone: false,
        order: 4,
        estimatedMinutes: 10,
        scientificNote: 'Dueño claro por tarea, estándares de "listo"',
      },
    ],
    tags: ['pareja', 'comunicación', 'gottman', 'logística'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: true,
    reminderTime: '18:00',
    emoji: '💑',
  },

  {
    id: 'prot-meal-prep',
    title: 'Batch Cooking / Prep Semanal',
    description: 'Preparar bases de comida para la semana',
    rationale:
      'Decidir qué cocinar cada día consume energía. Preparar bases el domingo reduce la fricción diaria y asegura alimentación vegana equilibrada.',
    frequency: 'weekly',
    layer: 'maintenance',
    priority: 'high',
    category: 'health',
    timeEstimate: 90,
    subtasks: [
      {
        id: 'sub-menu-plan',
        label: 'Planificar menú de la semana (almuerzos y cenas)',
        isDone: false,
        order: 0,
        estimatedMinutes: 15,
        scientificNote: 'Planificar menú reduce fatiga de decisiones y gasto impulsivo',
      },
      {
        id: 'sub-shopping-list',
        label: 'Lista de compras basada en menú',
        isDone: false,
        order: 1,
        estimatedMinutes: 10,
        scientificNote: 'Comprar con lista reduce compras impulsivas y desperdicio',
      },
      {
        id: 'sub-batch-grains',
        label: 'Cocinar granos/legumbres para 3-4 días',
        isDone: false,
        order: 2,
        estimatedMinutes: 25,
        scientificNote: 'Arroz, quinoa, lentejas, garbanzos',
      },
      {
        id: 'sub-batch-veggies',
        label: 'Lavar y cortar verduras para la semana',
        isDone: false,
        order: 3,
        estimatedMinutes: 20,
        scientificNote: 'Verduras pre-cortadas aumentan probabilidad de consumo',
      },
      {
        id: 'sub-batch-sauces',
        label: 'Preparar 1-2 salsas/aderezos',
        isDone: false,
        order: 4,
        estimatedMinutes: 15,
        scientificNote: 'Hummus, vinagreta, salsa de tomate',
      },
    ],
    tags: ['comida', 'vegano', 'preparación', 'meal-prep'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '🥗',
  },

  {
    id: 'prot-weekly-security',
    title: 'Ciberhigiene Semanal',
    description: 'Mantenimiento de seguridad digital básico',
    rationale:
      'CISA recomienda actualizaciones automáticas y MFA en todo. Una revisión semanal previene vulnerabilidades acumuladas.',
    frequency: 'weekly',
    layer: 'maintenance',
    priority: 'medium',
    category: 'tech',
    timeEstimate: 10,
    subtasks: [
      {
        id: 'sub-updates-check',
        label: 'Verificar actualizaciones pendientes (OS, apps críticas)',
        isDone: false,
        order: 0,
        estimatedMinutes: 3,
        scientificNote: 'Actualizaciones parchean vulnerabilidades de seguridad conocidas',
      },
      {
        id: 'sub-browser-cleanup',
        label: 'Limpiar extensiones/tabs innecesarios',
        isDone: false,
        order: 1,
        estimatedMinutes: 3,
        scientificNote: 'Extensiones son vector de ataque común; minimizar superficie',
      },
      {
        id: 'sub-downloads-clear',
        label: 'Vaciar carpeta de descargas',
        isDone: false,
        order: 2,
        estimatedMinutes: 3,
        scientificNote: 'Descargas acumuladas consumen espacio y pueden contener malware',
      },
    ],
    tags: ['seguridad', 'tecnología', 'mantenimiento'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '🔒',
  },

  {
    id: 'prot-weekly-calendar',
    title: 'Planificación Semanal',
    description: 'Definir metas y bloques de la semana',
    rationale:
      'Planificar cada domingo aumenta probabilidad de cumplimiento. Definir 1-3 metas clave en trabajo, estudio y vida personal reduce improvisación.',
    frequency: 'weekly',
    layer: 'growth',
    priority: 'high',
    category: 'self-care',
    timeEstimate: 30,
    subtasks: [
      {
        id: 'sub-review-calendar',
        label: 'Revisar calendario y compromisos de la semana',
        isDone: false,
        order: 0,
        estimatedMinutes: 5,
        scientificNote: 'Anticipar compromisos reduce sorpresas y permite preparación',
      },
      {
        id: 'sub-3-goals-work',
        label: 'Definir 1-3 entregables clave de trabajo',
        isDone: false,
        order: 1,
        estimatedMinutes: 5,
        scientificNote: 'Máximo 3 para evitar dispersión típica del TDAH',
      },
      {
        id: 'sub-3-goals-study',
        label: 'Definir 1-3 metas de estudio',
        isDone: false,
        order: 2,
        estimatedMinutes: 5,
        scientificNote: 'Metas específicas y medibles aumentan probabilidad de logro',
      },
      {
        id: 'sub-3-goals-life',
        label: 'Definir 1-3 metas personales (salud, pareja, casa)',
        isDone: false,
        order: 3,
        estimatedMinutes: 5,
        scientificNote: 'Balance trabajo-vida previene burnout',
      },
      {
        id: 'sub-block-time',
        label: 'Bloquear tiempos de foco en calendario',
        isDone: false,
        order: 4,
        estimatedMinutes: 5,
        scientificNote: 'Intenciones de implementación: cuándo y dónde',
      },
    ],
    tags: ['planificación', 'semanal', 'metas', 'calendario'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: true,
    reminderTime: '18:45',
    emoji: '📅',
  },

  // ============================================
  // PROTOCOLOS MENSUALES
  // ============================================

  {
    id: 'prot-monthly-review',
    title: 'Retrospectiva Mensual',
    description: 'Evaluación y ajuste de largo plazo',
    rationale:
      'El mes es la unidad de cambio significativo. Una retrospectiva mensual permite ver patrones invisibles en lo diario.',
    frequency: 'monthly',
    layer: 'growth',
    priority: 'high',
    category: 'self-care',
    timeEstimate: 45,
    subtasks: [
      {
        id: 'sub-3-wins',
        label: '3 logros medibles del mes',
        isDone: false,
        order: 0,
        estimatedMinutes: 10,
        scientificNote: 'Reconocer logros fortalece autoeficacia y motivación',
      },
      {
        id: 'sub-3-errors',
        label: '3 errores típicos que se repitieron',
        isDone: false,
        order: 1,
        estimatedMinutes: 10,
        scientificNote: 'Identificar patrones es primer paso para romperlos',
      },
      {
        id: 'sub-1-fix',
        label: '1 corrección sistémica para el próximo mes',
        isDone: false,
        order: 2,
        estimatedMinutes: 10,
        scientificNote: 'Un cambio a la vez es más sostenible que múltiples cambios',
      },
      {
        id: 'sub-focus',
        label: 'Elegir 1 foco de aprendizaje del mes (no 5)',
        isDone: false,
        order: 3,
        estimatedMinutes: 10,
        scientificNote: 'Foco único permite profundidad; dispersión produce superficialidad',
      },
    ],
    tags: ['retrospectiva', 'evaluación', 'ajuste'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '📈',
  },

  {
    id: 'prot-monthly-health',
    title: 'Chequeo de Salud',
    description: 'Revisión mensual de salud y citas',
    rationale:
      'La salud requiere mantenimiento preventivo. Registrar patrones y revisar citas evita emergencias evitables.',
    frequency: 'monthly',
    layer: 'survival',
    priority: 'high',
    category: 'health',
    timeEstimate: 20,
    subtasks: [
      {
        id: 'sub-appts',
        label: 'Revisar citas pendientes (médico, exámenes, dentista)',
        isDone: false,
        order: 0,
        estimatedMinutes: 5,
        scientificNote: 'Prevención > tratamiento. Citas regulares detectan problemas temprano',
      },
      {
        id: 'sub-patterns',
        label: 'Registrar patrones de sueño/energía del mes',
        isDone: false,
        order: 1,
        estimatedMinutes: 10,
        scientificNote: 'Datos objetivos revelan patrones que la percepción distorsiona',
      },
      {
        id: 'sub-meds-stock',
        label: 'Verificar stock de medicamentos',
        isDone: false,
        order: 2,
        estimatedMinutes: 3,
        scientificNote: 'Quedarse sin medicación interrumpe tratamiento',
      },
    ],
    tags: ['salud', 'citas', 'prevención'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '🏥',
  },

  {
    id: 'prot-monthly-finance',
    title: 'Cierre Financiero Mensual',
    description: 'Balance y ajuste de presupuesto',
    rationale:
      'El cierre mensual revela la realidad vs. la intención. Ajustar el presupuesto con datos reales previene deudas y estrés.',
    frequency: 'monthly',
    layer: 'maintenance',
    priority: 'high',
    category: 'finance',
    timeEstimate: 30,
    subtasks: [
      {
        id: 'sub-close',
        label: 'Cierre: cuánto entró, cuánto salió, cuánto se ahorró',
        isDone: false,
        order: 0,
        estimatedMinutes: 10,
        scientificNote: 'Balance mensual revela realidad vs percepción de gastos',
      },
      {
        id: 'sub-adjust-budget',
        label: 'Ajustar presupuesto del mes siguiente',
        isDone: false,
        order: 1,
        estimatedMinutes: 10,
        scientificNote: 'Presupuesto basado en datos reales > estimaciones optimistas',
      },
      {
        id: 'sub-decision',
        label: '1 decisión financiera (ahorro, deuda, compra planificada)',
        isDone: false,
        order: 2,
        estimatedMinutes: 10,
        scientificNote: 'Decisiones financieras deliberadas > impulsos',
      },
    ],
    tags: ['finanzas', 'presupuesto', 'ahorro'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '📊',
  },

  {
    id: 'prot-monthly-deep-clean',
    title: 'Limpieza Profunda Mensual',
    description: 'Mantenimiento profundo del hogar',
    rationale:
      'Algunas zonas requieren atención mensual. La limpieza profunda previene degradación acumulada.',
    frequency: 'monthly',
    layer: 'maintenance',
    priority: 'medium',
    category: 'home',
    timeEstimate: 90,
    subtasks: [
      {
        id: 'sub-kitchen-deep',
        label: 'Cocina: campana, horno, microondas, despensa',
        isDone: false,
        order: 0,
        estimatedMinutes: 25,
        scientificNote: 'Grasa acumulada en campana es riesgo de incendio',
      },
      {
        id: 'sub-fridge-deep',
        label: 'Refrigerador: vaciar, limpiar, reorganizar',
        isDone: false,
        order: 1,
        estimatedMinutes: 20,
        scientificNote: 'Refrigerador limpio mantiene alimentos seguros y reduce olores',
      },
      {
        id: 'sub-purge',
        label: 'Depuración: 15-30 ítems fuera (donar/botar)',
        isDone: false,
        order: 2,
        estimatedMinutes: 20,
        scientificNote: 'Menos objetos = menos carga cognitiva de gestión',
      },
      {
        id: 'sub-litter-wash',
        label: 'Lavado profundo de areneros',
        isDone: false,
        order: 3,
        estimatedMinutes: 15,
        scientificNote: 'Lavado mensual elimina olores y bacterias acumuladas',
      },
      {
        id: 'sub-inventory',
        label: 'Inventario de reposición (arena, comida, limpieza)',
        isDone: false,
        order: 4,
        estimatedMinutes: 5,
        scientificNote: 'Inventario mensual previene quedarse sin suministros críticos',
      },
    ],
    tags: ['limpieza', 'hogar', 'mantenimiento'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '✨',
  },

  {
    id: 'prot-monthly-pets',
    title: 'Cuidado Mensual Mascotas',
    description: 'Mantenimiento mensual de Philippa y Fjor',
    rationale:
      'Algunos cuidados de mascotas son mensuales: cepillado profundo, revisión de uñas, peso. Previene problemas acumulados.',
    frequency: 'monthly',
    layer: 'maintenance',
    priority: 'high',
    category: 'pets',
    timeEstimate: 30,
    subtasks: [
      {
        id: 'sub-nails',
        label: 'Revisar uñas (cortar si necesario)',
        isDone: false,
        order: 0,
        estimatedMinutes: 5,
        scientificNote: 'Uñas largas pueden causar lesiones y problemas al caminar',
      },
      {
        id: 'sub-brush-deep',
        label: 'Cepillado profundo (especialmente Fjor)',
        isDone: false,
        order: 1,
        estimatedMinutes: 10,
        scientificNote: 'Reduce bolas de pelo y distribuye aceites naturales del pelaje',
      },
      {
        id: 'sub-weight-month',
        label: 'Pesar o evaluar condición corporal',
        isDone: false,
        order: 2,
        estimatedMinutes: 5,
        scientificNote: 'Cambios de peso pueden indicar problemas',
      },
      {
        id: 'sub-ears-eyes',
        label: 'Revisar orejas y ojos',
        isDone: false,
        order: 3,
        estimatedMinutes: 5,
        scientificNote: 'Detectar infecciones o irritaciones temprano',
      },
    ],
    tags: ['mascotas', 'cuidado', 'salud'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '🐾',
  },

  {
    id: 'prot-monthly-tech',
    title: 'Mantenimiento Técnico Mensual',
    description: 'Seguridad y backups de sistemas',
    rationale:
      'La seguridad y los backups requieren atención regular. Una revisión mensual previene pérdida de datos y vulnerabilidades.',
    frequency: 'monthly',
    layer: 'maintenance',
    priority: 'medium',
    category: 'tech',
    timeEstimate: 30,
    subtasks: [
      {
        id: 'sub-security',
        label: 'Revisión de seguridad: accesos, usuarios, llaves',
        isDone: false,
        order: 0,
        estimatedMinutes: 10,
        scientificNote: 'Accesos no usados son vectores de ataque potenciales',
      },
      {
        id: 'sub-backup',
        label: 'Verificar backups (que existan y restauren)',
        isDone: false,
        order: 1,
        estimatedMinutes: 10,
        scientificNote: 'Backup sin prueba de restauración = backup inexistente',
      },
      {
        id: 'sub-updates',
        label: 'Actualizaciones planificadas (en ventana segura)',
        isDone: false,
        order: 2,
        estimatedMinutes: 10,
        scientificNote: 'Actualizaciones en horarios no críticos reducen riesgo de downtime',
      },
    ],
    tags: ['tecnología', 'seguridad', 'backups'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '🔐',
  },

  {
    id: 'prot-monthly-subs',
    title: 'Auditoría de Suscripciones',
    description: 'Revisar y recortar servicios recurrentes',
    rationale:
      'Las suscripciones se acumulan invisiblemente. Una auditoría mensual detecta gastos hormiga y servicios que ya no aportan valor.',
    frequency: 'monthly',
    layer: 'maintenance',
    priority: 'medium',
    category: 'finance',
    timeEstimate: 15,
    subtasks: [
      {
        id: 'sub-list-subs',
        label: 'Listar todas las suscripciones activas',
        isDone: false,
        order: 0,
        estimatedMinutes: 5,
        scientificNote: 'Hacer visible el gasto oculto es primer paso para controlarlo',
      },
      {
        id: 'sub-eval-subs',
        label: '¿Realmente usé cada una este mes?',
        isDone: false,
        order: 1,
        estimatedMinutes: 3,
        scientificNote: 'Uso real vs intención de uso revela valor verdadero',
      },
      {
        id: 'sub-cancel-subs',
        label: 'Cancelar al menos 1 que no aporte valor',
        isDone: false,
        order: 2,
        estimatedMinutes: 5,
        scientificNote: 'Si dudas, cancela. Siempre puedes reactivar.',
      },
      {
        id: 'sub-free-trials',
        label: 'Revisar pruebas gratuitas activas (evitar cobros sorpresa)',
        isDone: false,
        order: 3,
        estimatedMinutes: 2,
        scientificNote: 'Trials olvidados son fuente común de cobros inesperados',
      },
    ],
    tags: ['finanzas', 'suscripciones', 'gastos'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '📱',
  },

  {
    id: 'prot-monthly-body',
    title: 'Tracking de Salud Personal',
    description: 'Registro mensual de estado físico',
    rationale:
      'Los cambios graduales de peso y síntomas pasan desapercibidos. Un registro mensual permite detectar patrones y llevar datos concretos al médico.',
    frequency: 'monthly',
    layer: 'survival',
    priority: 'high',
    category: 'health',
    timeEstimate: 15,
    subtasks: [
      {
        id: 'sub-weight',
        label: 'Registrar peso (tendencia, no obsesión)',
        isDone: false,
        order: 0,
        estimatedMinutes: 2,
        scientificNote: 'Cambios >2kg sostenidos merecen atención',
      },
      {
        id: 'sub-symptoms',
        label: 'Anotar síntomas nuevos o recurrentes del mes',
        isDone: false,
        order: 1,
        estimatedMinutes: 5,
        scientificNote: 'Registro de síntomas ayuda al médico en diagnóstico',
      },
      {
        id: 'sub-sleep-pattern',
        label: 'Evaluar patrón de sueño del mes (¿dormí las 7h?)',
        isDone: false,
        order: 2,
        estimatedMinutes: 3,
        scientificNote: 'Déficit de sueño acumulado afecta cognición y salud',
      },
      {
        id: 'sub-energy-pattern',
        label: 'Evaluar patrón de energía (¿hubo días de agotamiento recurrente?)',
        isDone: false,
        order: 3,
        estimatedMinutes: 3,
        scientificNote: 'Fatiga crónica puede indicar problemas de salud subyacentes',
      },
      {
        id: 'sub-meds-effect',
        label: 'Evaluar efectos de medicación (reportar al médico si hay cambios)',
        isDone: false,
        order: 4,
        estimatedMinutes: 3,
        scientificNote: 'Ajustes de medicación requieren datos objetivos de efectividad',
      },
    ],
    tags: ['salud', 'tracking', 'peso', 'síntomas'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '📋',
  },

  {
    id: 'prot-monthly-documents',
    title: 'Organización de Documentos',
    description: 'Mantener documentos críticos ordenados',
    rationale:
      'Los documentos desorganizados generan estrés cuando se necesitan urgente. Una carpeta única con subcarpetas evita búsquedas desesperadas.',
    frequency: 'monthly',
    layer: 'maintenance',
    priority: 'low',
    category: 'home',
    timeEstimate: 20,
    subtasks: [
      {
        id: 'sub-scan-docs',
        label: 'Escanear/digitalizar documentos físicos nuevos',
        isDone: false,
        order: 0,
        estimatedMinutes: 5,
        scientificNote: 'Documentos digitales tienen backup; físicos se pierden',
      },
      {
        id: 'sub-organize-docs',
        label: 'Organizar en carpetas: Identidad, Salud, Finanzas, Vivienda, Trabajo',
        isDone: false,
        order: 1,
        estimatedMinutes: 5,
        scientificNote: 'Categorización clara permite encontrar documentos en emergencias',
      },
      {
        id: 'sub-backup-docs',
        label: 'Verificar respaldo en la nube',
        isDone: false,
        order: 2,
        estimatedMinutes: 3,
        scientificNote: 'Regla 3-2-1: 3 copias, 2 medios, 1 offsite',
      },
      {
        id: 'sub-expiry-docs',
        label: 'Revisar documentos próximos a vencer (licencia, seguros, etc.)',
        isDone: false,
        order: 3,
        estimatedMinutes: 5,
        scientificNote: 'Renovar antes del vencimiento evita multas y problemas legales',
      },
    ],
    tags: ['documentos', 'organización', 'backup'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '📁',
  },

  // ============================================
  // PROTOCOLOS TRIMESTRALES
  // ============================================

  {
    id: 'prot-quarterly-review',
    title: 'Retrospectiva Trimestral',
    description: 'Evaluación de objetivos de mediano plazo',
    rationale:
      'El trimestre es la unidad de cambio estratégico. Permite evaluar proyectos completos, ajustar dirección de carrera/estudio, y detectar patrones que el mes no revela.',
    frequency: 'quarterly',
    layer: 'growth',
    priority: 'high',
    category: 'self-care',
    timeEstimate: 60,
    subtasks: [
      {
        id: 'sub-q-goals',
        label: '¿Cumplí los objetivos del trimestre? ¿Por qué sí/no?',
        isDone: false,
        order: 0,
        estimatedMinutes: 15,
        scientificNote: 'Análisis de causas permite ajustar estrategia, no solo esfuerzo',
      },
      {
        id: 'sub-q-patterns',
        label: 'Identificar 3 patrones que se repitieron (buenos y malos)',
        isDone: false,
        order: 1,
        estimatedMinutes: 15,
        scientificNote: 'Patrones trimestrales revelan tendencias invisibles en lo diario',
      },
      {
        id: 'sub-q-next',
        label: 'Definir 1-3 objetivos para el próximo trimestre',
        isDone: false,
        order: 2,
        estimatedMinutes: 15,
        scientificNote: 'Máximo 3 para evitar dispersión',
      },
      {
        id: 'sub-q-adjust',
        label: 'Ajustar estrategia de estudio/trabajo según resultados',
        isDone: false,
        order: 3,
        estimatedMinutes: 15,
        scientificNote: 'Estrategia basada en datos > intuición o persistencia ciega',
      },
    ],
    tags: ['retrospectiva', 'objetivos', 'trimestre'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '🎯',
  },

  {
    id: 'prot-quarterly-hygiene',
    title: 'Reposición de Higiene Trimestral',
    description: 'Cambio de elementos que caducan cada 3 meses',
    rationale:
      'Los cepillos de dientes pierden efectividad a los 3 meses según la ADA. Los filtros y esponjas acumulan bacterias. Renovar previene problemas de salud.',
    frequency: 'quarterly',
    layer: 'survival',
    priority: 'high',
    category: 'hygiene',
    timeEstimate: 15,
    subtasks: [
      {
        id: 'sub-toothbrush',
        label: 'Cambiar cepillo de dientes',
        isDone: false,
        order: 0,
        estimatedMinutes: 2,
        isCritical: true,
        scientificNote: 'ADA recomienda cada 3-4 meses o antes si las cerdas están dañadas',
      },
      {
        id: 'sub-sponge',
        label: 'Cambiar esponja de cocina',
        isDone: false,
        order: 1,
        estimatedMinutes: 2,
      },
      {
        id: 'sub-filters',
        label: 'Revisar/cambiar filtros (agua, aire, aspiradora)',
        isDone: false,
        order: 2,
        estimatedMinutes: 5,
      },
      {
        id: 'sub-razors',
        label: 'Revisar estado de cuchillas/rasuradoras',
        isDone: false,
        order: 3,
        estimatedMinutes: 2,
      },
    ],
    tags: ['higiene', 'reposición', 'salud'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '🪥',
  },

  {
    id: 'prot-quarterly-health',
    title: 'Revisión de Medicación',
    description: 'Seguimiento de efectividad de tratamientos actuales',
    rationale:
      'El seguimiento regular de la medicación (Escitalopram, Topiramato) permite ajustar dosis y detectar efectos secundarios temprano.',
    frequency: 'quarterly',
    layer: 'survival',
    priority: 'high',
    category: 'health',
    timeEstimate: 20,
    subtasks: [
      {
        id: 'sub-meds-review',
        label: 'Revisar efectividad de medicación actual',
        isDone: false,
        order: 0,
        estimatedMinutes: 10,
        scientificNote: 'Evaluar: ánimo, sueño, migrañas, efectos secundarios',
      },
      {
        id: 'sub-symptoms',
        label: 'Documentar síntomas recurrentes del trimestre',
        isDone: false,
        order: 1,
        estimatedMinutes: 5,
      },
      {
        id: 'sub-schedule',
        label: 'Agendar cita de control si es necesario',
        isDone: false,
        order: 2,
        estimatedMinutes: 5,
      },
    ],
    tags: ['salud', 'médico', 'medicación'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '💊',
  },

  {
    id: 'prot-quarterly-pets',
    title: 'Desparasitación Trimestral',
    description: 'Desparasitación preventiva de Philippa y Fjor',
    rationale:
      'Los gatos indoor también necesitan desparasitación trimestral. Pueden infectarse por huevos en zapatos, tierra de plantas, o presas ocasionales (insectos).',
    frequency: 'quarterly',
    layer: 'survival',
    priority: 'high',
    category: 'pets',
    timeEstimate: 15,
    subtasks: [
      {
        id: 'sub-deworming',
        label: 'Aplicar desparasitante (pipeta o pastilla)',
        isDone: false,
        order: 0,
        estimatedMinutes: 5,
        scientificNote: 'Seguir indicación del veterinario según peso',
      },
      {
        id: 'sub-weight-check',
        label: 'Registrar peso actual de cada gata',
        isDone: false,
        order: 1,
        estimatedMinutes: 5,
      },
      {
        id: 'sub-supplies',
        label: 'Verificar stock de desparasitante para próximo trimestre',
        isDone: false,
        order: 2,
        estimatedMinutes: 3,
      },
    ],
    tags: ['mascotas', 'desparasitación', 'salud'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '💊',
  },

  {
    id: 'prot-quarterly-career',
    title: 'Revisión de Carrera/Estudio',
    description: 'Evaluación de progreso profesional y académico',
    rationale:
      'Los trimestres universitarios y laborales son ciclos naturales. Evaluar al cierre permite ajustar antes del siguiente período.',
    frequency: 'quarterly',
    layer: 'growth',
    priority: 'high',
    category: 'work',
    timeEstimate: 45,
    subtasks: [
      {
        id: 'sub-uni-eval',
        label: 'Evaluar rendimiento del trimestre universitario',
        isDone: false,
        order: 0,
        estimatedMinutes: 10,
      },
      {
        id: 'sub-work-eval',
        label: 'Evaluar proyectos de trabajo completados',
        isDone: false,
        order: 1,
        estimatedMinutes: 10,
      },
      {
        id: 'sub-cv-update',
        label: 'Actualizar CV/LinkedIn con logros del trimestre',
        isDone: false,
        order: 2,
        estimatedMinutes: 15,
      },
      {
        id: 'sub-portfolio',
        label: 'Añadir 1 pieza al portafolio',
        isDone: false,
        order: 3,
        estimatedMinutes: 20,
      },
      {
        id: 'sub-skills',
        label: 'Identificar 1 skill a desarrollar el próximo trimestre',
        isDone: false,
        order: 4,
        estimatedMinutes: 5,
      },
    ],
    tags: ['carrera', 'estudio', 'portafolio'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '📈',
  },

  {
    id: 'prot-quarterly-seasonal',
    title: 'Cambio de Estación',
    description: 'Ajustes del hogar por cambio estacional',
    rationale:
      'Cada estación trae necesidades diferentes. Prepararse previene incomodidad y gastos de emergencia.',
    frequency: 'quarterly',
    layer: 'maintenance',
    priority: 'medium',
    category: 'home',
    timeEstimate: 60,
    subtasks: [
      {
        id: 'sub-wardrobe',
        label: 'Rotación de ropa de temporada',
        isDone: false,
        order: 0,
        estimatedMinutes: 15,
      },
      {
        id: 'sub-purge-clothes',
        label: 'Depurar ropa que ya no sirve (donar/botar)',
        isDone: false,
        order: 1,
        estimatedMinutes: 15,
      },
      {
        id: 'sub-bedding',
        label: 'Cambiar ropa de cama según temperatura',
        isDone: false,
        order: 2,
        estimatedMinutes: 10,
      },
      {
        id: 'sub-deep-corners',
        label: 'Limpieza de rincones olvidados (detrás de muebles, ventanas)',
        isDone: false,
        order: 3,
        estimatedMinutes: 15,
      },
      {
        id: 'sub-mattress',
        label: 'Rotar colchón',
        isDone: false,
        order: 4,
        estimatedMinutes: 5,
        scientificNote: 'Extiende vida útil y previene deformación',
      },
    ],
    tags: ['hogar', 'estación', 'organización'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '🍂',
  },

  {
    id: 'prot-quarterly-finance',
    title: 'Revisión Financiera Trimestral',
    description: 'Balance financiero de mediano plazo',
    rationale:
      'El trimestre permite ver tendencias de gasto y ahorro que el mes oculta. Es el momento de ajustar estrategia financiera.',
    frequency: 'quarterly',
    layer: 'maintenance',
    priority: 'high',
    category: 'finance',
    timeEstimate: 45,
    subtasks: [
      {
        id: 'sub-q-balance',
        label: 'Balance trimestral: ingresos vs gastos totales',
        isDone: false,
        order: 0,
        estimatedMinutes: 15,
      },
      {
        id: 'sub-q-savings',
        label: 'Evaluar progreso de ahorro vs meta',
        isDone: false,
        order: 1,
        estimatedMinutes: 10,
      },
      {
        id: 'sub-q-subs-audit',
        label: 'Auditoría de suscripciones: ¿realmente las uso?',
        isDone: false,
        order: 2,
        estimatedMinutes: 5,
        scientificNote: 'Cancelar lo que no aporta valor',
      },
      {
        id: 'sub-q-invest',
        label: 'Revisar inversiones/deudas',
        isDone: false,
        order: 3,
        estimatedMinutes: 10,
      },
      {
        id: 'sub-q-goals-fin',
        label: 'Ajustar metas financieras del próximo trimestre',
        isDone: false,
        order: 4,
        estimatedMinutes: 10,
      },
    ],
    tags: ['finanzas', 'ahorro', 'inversión'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '💰',
  },

  {
    id: 'prot-quarterly-tech',
    title: 'Auditoría Técnica Trimestral',
    description: 'Revisión profunda de sistemas y seguridad',
    rationale:
      'La seguridad digital requiere revisión periódica. Contraseñas, accesos y backups necesitan atención cada trimestre.',
    frequency: 'quarterly',
    layer: 'maintenance',
    priority: 'high',
    category: 'tech',
    timeEstimate: 60,
    subtasks: [
      {
        id: 'sub-passwords',
        label: 'Rotar contraseñas críticas (banco, email principal)',
        isDone: false,
        order: 0,
        estimatedMinutes: 15,
        scientificNote: 'Usar password manager para generar y almacenar',
      },
      {
        id: 'sub-2fa',
        label: 'Verificar que 2FA esté activo en cuentas importantes',
        isDone: false,
        order: 1,
        estimatedMinutes: 10,
      },
      {
        id: 'sub-backup-test',
        label: 'Probar restauración de backup (no solo verificar que existe)',
        isDone: false,
        order: 2,
        estimatedMinutes: 15,
        isCritical: true,
      },
      {
        id: 'sub-access-audit',
        label: 'Revisar apps con acceso a cuentas (Google, GitHub, etc.)',
        isDone: false,
        order: 3,
        estimatedMinutes: 10,
      },
      {
        id: 'sub-vps-audit',
        label: 'Auditoría de VPS: usuarios, puertos, servicios innecesarios',
        isDone: false,
        order: 4,
        estimatedMinutes: 15,
      },
    ],
    tags: ['tecnología', 'seguridad', 'backups'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '🔐',
  },

  {
    id: 'prot-quarterly-premortem',
    title: 'Premortem de Proyectos',
    description: 'Anticipar fallos antes de que ocurran',
    rationale:
      'Técnica de Harvard Business Review: imaginar que el proyecto fracasó y enumerar por qué. Contrarresta sesgos de sobreconfianza y previene errores evitables.',
    frequency: 'quarterly',
    layer: 'growth',
    priority: 'high',
    category: 'self-care',
    timeEstimate: 30,
    subtasks: [
      {
        id: 'sub-list-projects',
        label: 'Listar proyectos importantes de los próximos 90 días',
        isDone: false,
        order: 0,
        estimatedMinutes: 5,
      },
      {
        id: 'sub-imagine-fail',
        label: 'Imaginar que cada uno fracasó: ¿por qué?',
        isDone: false,
        order: 1,
        estimatedMinutes: 10,
        scientificNote: 'Técnica premortem de Gary Klein',
      },
      {
        id: 'sub-prevent',
        label: 'Definir 1-2 acciones preventivas por proyecto',
        isDone: false,
        order: 2,
        estimatedMinutes: 10,
      },
      {
        id: 'sub-calendar-blocks',
        label: 'Bloquear tiempo para las acciones preventivas',
        isDone: false,
        order: 3,
        estimatedMinutes: 5,
      },
    ],
    tags: ['premortem', 'planificación', 'proyectos', 'prevención'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '🔮',
  },

  {
    id: 'prot-quarterly-emergency',
    title: 'Kit de Emergencia y Seguridad',
    description: 'Verificar elementos de seguridad física',
    rationale:
      'Los elementos de emergencia se degradan o vencen. Una revisión trimestral asegura que estén funcionales cuando se necesiten.',
    frequency: 'quarterly',
    layer: 'survival',
    priority: 'high',
    category: 'home',
    timeEstimate: 30,
    subtasks: [
      {
        id: 'sub-first-aid',
        label: 'Revisar botiquín: medicamentos vencidos, faltantes',
        isDone: false,
        order: 0,
        estimatedMinutes: 10,
        isCritical: true,
      },
      {
        id: 'sub-emergency-numbers',
        label: 'Números de emergencia visibles y actualizados',
        isDone: false,
        order: 1,
        estimatedMinutes: 5,
        scientificNote: 'Bomberos, ambulancia, veterinario, familiar de confianza',
      },
      {
        id: 'sub-spare-keys',
        label: 'Verificar copia de llaves (ubicación, funcionamiento)',
        isDone: false,
        order: 2,
        estimatedMinutes: 5,
      },
      {
        id: 'sub-pet-emergency',
        label: 'Kit de emergencia para gatos (transportadora, info médica)',
        isDone: false,
        order: 3,
        estimatedMinutes: 10,
      },
    ],
    tags: ['emergencia', 'seguridad', 'hogar', 'prevención'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '🆘',
  },

  {
    id: 'prot-quarterly-savings',
    title: 'Revisión Fondo de Emergencia',
    description: 'Evaluar y ajustar ahorro de contingencia',
    rationale:
      'El fondo de emergencia es protección contra imprevistos. Meta escalonada: empezar por 1 mes, avanzar a 3-6 según contexto. Sin fondo = vulnerabilidad alta.',
    frequency: 'quarterly',
    layer: 'maintenance',
    priority: 'high',
    category: 'finance',
    timeEstimate: 20,
    subtasks: [
      {
        id: 'sub-current-fund',
        label: 'Calcular cuánto tengo acumulado actualmente',
        isDone: false,
        order: 0,
        estimatedMinutes: 5,
      },
      {
        id: 'sub-months-covered',
        label: 'Calcular cuántos meses de gastos cubre',
        isDone: false,
        order: 1,
        estimatedMinutes: 5,
        scientificNote: 'Meta: mínimo 1 mes, ideal 3-6 meses',
      },
      {
        id: 'sub-adjust-goal',
        label: 'Ajustar meta según situación laboral actual',
        isDone: false,
        order: 2,
        estimatedMinutes: 5,
      },
      {
        id: 'sub-automate-savings',
        label: 'Verificar que ahorro automático sigue activo',
        isDone: false,
        order: 3,
        estimatedMinutes: 3,
        scientificNote: 'Automatizar > depender de voluntad',
      },
    ],
    tags: ['finanzas', 'ahorro', 'emergencia', 'seguridad'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '🏦',
  },

  {
    id: 'prot-quarterly-relationships',
    title: 'Auditoría de Relaciones',
    description: 'Evaluar estado de vínculos significativos',
    rationale:
      'Las conexiones sociales consistentes se asocian a menor mortalidad. 3-5 vínculos clave requieren mantenimiento intencional.',
    frequency: 'quarterly',
    layer: 'growth',
    priority: 'medium',
    category: 'social',
    timeEstimate: 20,
    subtasks: [
      {
        id: 'sub-list-5',
        label: 'Listar 3-5 personas más importantes en tu vida',
        isDone: false,
        order: 0,
        estimatedMinutes: 5,
      },
      {
        id: 'sub-last-contact',
        label: '¿Cuándo fue el último contacto significativo con cada una?',
        isDone: false,
        order: 1,
        estimatedMinutes: 5,
      },
      {
        id: 'sub-neglected',
        label: 'Identificar relaciones descuidadas',
        isDone: false,
        order: 2,
        estimatedMinutes: 3,
      },
      {
        id: 'sub-plan-contact',
        label: 'Planificar 1 contacto intencional con cada una este trimestre',
        isDone: false,
        order: 3,
        estimatedMinutes: 5,
      },
    ],
    tags: ['relaciones', 'social', 'vínculos', 'comunidad'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '👥',
  },

  // ============================================
  // PROTOCOLOS ANUALES
  // ============================================

  {
    id: 'prot-yearly-health',
    title: 'Chequeo Médico Anual',
    description: 'Revisión preventiva completa de salud',
    rationale:
      'Los chequeos anuales detectan problemas antes de que se agraven. Incluye dental, dermatológico y exámenes de rutina según edad y factores de riesgo.',
    frequency: 'yearly',
    layer: 'survival',
    priority: 'critical',
    category: 'health',
    timeEstimate: 60,
    subtasks: [
      {
        id: 'sub-dental',
        label: 'Agendar y asistir a cita dental (limpieza + revisión)',
        isDone: false,
        order: 0,
        estimatedMinutes: 60,
        scientificNote: 'ADA recomienda limpieza profesional cada 6-12 meses',
        isCritical: true,
      },
      {
        id: 'sub-derma',
        label: 'Revisión dermatológica (lunares, piel)',
        isDone: false,
        order: 1,
        estimatedMinutes: 45,
        scientificNote: 'Detección temprana de melanoma y problemas cutáneos',
      },
      {
        id: 'sub-blood',
        label: 'Exámenes de sangre (hemograma, perfil lipídico, glucosa)',
        isDone: false,
        order: 2,
        estimatedMinutes: 30,
      },
      {
        id: 'sub-eye',
        label: 'Chequeo oftalmológico',
        isDone: false,
        order: 3,
        estimatedMinutes: 45,
        scientificNote: 'Importante con trabajo remoto y pantallas',
      },
      {
        id: 'sub-gyneco',
        label: 'Chequeo ginecológico anual',
        isDone: false,
        order: 4,
        estimatedMinutes: 45,
        isCritical: true,
      },
    ],
    tags: ['salud', 'médico', 'prevención', 'anual'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '🏥',
  },

  {
    id: 'prot-yearly-pets',
    title: 'Vacunación Anual Gatas',
    description: 'Vacunas anuales para Philippa y Fjor',
    rationale:
      'Gatos indoor requieren vacunación anual (triple felina). Mantiene protección contra enfermedades virales graves. Incluye chequeo veterinario completo.',
    frequency: 'yearly',
    layer: 'survival',
    priority: 'critical',
    category: 'pets',
    timeEstimate: 45,
    subtasks: [
      {
        id: 'sub-schedule',
        label: 'Agendar cita veterinaria para vacunación',
        isDone: false,
        order: 0,
        estimatedMinutes: 5,
        isCritical: true,
      },
      {
        id: 'sub-philippa-vaccine',
        label: 'Vacuna triple felina Philippa (2y 10m)',
        isDone: false,
        order: 1,
        estimatedMinutes: 30,
        scientificNote: 'FVRCP: Rinotraqueítis, Calicivirus, Panleucopenia',
        isCritical: true,
      },
      {
        id: 'sub-fjor-vaccine',
        label: 'Vacuna triple felina Fjor (5m)',
        isDone: false,
        order: 2,
        estimatedMinutes: 30,
        scientificNote: 'Verificar calendario completo de primovacunación',
        isCritical: true,
      },
      {
        id: 'sub-checkup',
        label: 'Chequeo veterinario general (peso, dientes, oídos)',
        isDone: false,
        order: 3,
        estimatedMinutes: 20,
      },
      {
        id: 'sub-record',
        label: 'Actualizar carnet de vacunación',
        isDone: false,
        order: 4,
        estimatedMinutes: 3,
      },
    ],
    tags: ['mascotas', 'vacunas', 'veterinario', 'anual'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '💉',
  },

  {
    id: 'prot-yearly-admin',
    title: 'Revisión Documental Anual',
    description: 'Organización y actualización de documentos importantes',
    rationale:
      'Los documentos se desactualizan y las contraseñas caducan. Una revisión anual previene emergencias administrativas.',
    frequency: 'yearly',
    layer: 'maintenance',
    priority: 'high',
    category: 'home',
    timeEstimate: 60,
    subtasks: [
      {
        id: 'sub-id',
        label: 'Verificar vigencia de documentos de identidad',
        isDone: false,
        order: 0,
        estimatedMinutes: 10,
        scientificNote: 'Cédula, pasaporte, licencia de conducir',
        isCritical: true,
      },
      {
        id: 'sub-insurance',
        label: 'Revisar seguros (salud, hogar, mascota)',
        isDone: false,
        order: 1,
        estimatedMinutes: 15,
      },
      {
        id: 'sub-contracts',
        label: 'Revisar contratos activos (arriendo, servicios)',
        isDone: false,
        order: 2,
        estimatedMinutes: 15,
      },
      {
        id: 'sub-backup',
        label: 'Backup completo de documentos digitales',
        isDone: false,
        order: 3,
        estimatedMinutes: 20,
      },
      {
        id: 'sub-declutter',
        label: 'Eliminar documentos obsoletos',
        isDone: false,
        order: 4,
        estimatedMinutes: 15,
      },
    ],
    tags: ['documentos', 'administración', 'hogar'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '📋',
  },

  {
    id: 'prot-yearly-reflection',
    title: 'Reflexión y Planificación Anual',
    description: 'Balance del año y objetivos para el siguiente',
    rationale:
      'El cierre de año es un momento natural para evaluar logros, aprender de errores y establecer dirección. Reduce ansiedad y da claridad.',
    frequency: 'yearly',
    layer: 'growth',
    priority: 'high',
    category: 'self-care',
    timeEstimate: 90,
    subtasks: [
      {
        id: 'sub-wins',
        label: 'Listar 10+ logros del año',
        isDone: false,
        order: 0,
        estimatedMinutes: 20,
        scientificNote: 'Reconocer logros mejora autoeficacia',
      },
      {
        id: 'sub-lessons',
        label: 'Identificar 3-5 lecciones aprendidas',
        isDone: false,
        order: 1,
        estimatedMinutes: 15,
      },
      {
        id: 'sub-gratitude',
        label: 'Escribir carta de gratitud (a ti mismo o a otros)',
        isDone: false,
        order: 2,
        estimatedMinutes: 20,
      },
      {
        id: 'sub-goals',
        label: 'Definir 3 áreas de enfoque para el próximo año',
        isDone: false,
        order: 3,
        estimatedMinutes: 15,
        scientificNote: 'Menos es más: 3 áreas manejables',
      },
      {
        id: 'sub-word',
        label: 'Elegir una palabra/tema guía para el año',
        isDone: false,
        order: 4,
        estimatedMinutes: 10,
      },
    ],
    tags: ['reflexión', 'objetivos', 'crecimiento'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '🌟',
  },

  {
    id: 'prot-yearly-digital',
    title: 'Limpieza Digital Anual',
    description: 'Purga y organización del ecosistema digital',
    rationale:
      'La acumulación digital genera estrés y riesgos de seguridad. Una limpieza anual mantiene el sistema funcional y seguro.',
    frequency: 'yearly',
    layer: 'maintenance',
    priority: 'medium',
    category: 'tech',
    timeEstimate: 120,
    subtasks: [
      {
        id: 'sub-email',
        label: 'Limpiar bandejas de email (unsubscribe masivo)',
        isDone: false,
        order: 0,
        estimatedMinutes: 30,
      },
      {
        id: 'sub-photos',
        label: 'Organizar y respaldar fotos del año',
        isDone: false,
        order: 1,
        estimatedMinutes: 45,
      },
      {
        id: 'sub-apps',
        label: 'Eliminar apps no usadas en últimos 6 meses',
        isDone: false,
        order: 2,
        estimatedMinutes: 15,
      },
      {
        id: 'sub-passwords',
        label: 'Auditar y actualizar contraseñas críticas',
        isDone: false,
        order: 3,
        estimatedMinutes: 30,
        scientificNote: 'Gestor de contraseñas + MFA en todo',
        isCritical: true,
      },
      {
        id: 'sub-storage',
        label: 'Liberar espacio en la nube y disco local',
        isDone: false,
        order: 4,
        estimatedMinutes: 30,
      },
    ],
    tags: ['digital', 'organización', 'seguridad'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '🧹',
  },

  {
    id: 'prot-yearly-home',
    title: 'Mantenimiento Mayor del Hogar',
    description: 'Tareas anuales de mantenimiento doméstico',
    rationale:
      'Algunas tareas de hogar requieren atención anual. Prevenir es más económico que reparar emergencias.',
    frequency: 'yearly',
    layer: 'maintenance',
    priority: 'high',
    category: 'home',
    timeEstimate: 180,
    subtasks: [
      {
        id: 'sub-deep-clean',
        label: 'Limpieza profunda de zonas olvidadas (detrás de muebles, filtros)',
        isDone: false,
        order: 0,
        estimatedMinutes: 45,
      },
      {
        id: 'sub-mattress',
        label: 'Voltear/rotar colchón',
        isDone: false,
        order: 1,
        estimatedMinutes: 10,
        scientificNote: 'Extiende vida útil del colchón',
      },
      {
        id: 'sub-closet',
        label: 'Declutter de ropa y objetos (regla: 1 año sin uso = fuera)',
        isDone: false,
        order: 2,
        estimatedMinutes: 60,
      },
      {
        id: 'sub-appliances',
        label: 'Revisar estado de electrodomésticos grandes',
        isDone: false,
        order: 3,
        estimatedMinutes: 15,
      },
      {
        id: 'sub-plants',
        label: 'Trasplante de plantas que lo necesiten',
        isDone: false,
        order: 4,
        estimatedMinutes: 30,
      },
    ],
    tags: ['hogar', 'limpieza', 'mantenimiento'],
    isCompleted: false,
    streak: 0,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isCustom: false,
    reminderEnabled: false,
    emoji: '🏠',
  },
];
